/**
 * JUST MAIL PAGE
 * 
 * Standalone mailing feature - mail documents to credit bureaus without using the wizard
 * - Upload PDF documents
 * - Generate Handwritten Declaration with client info
 * - Reorder documents via drag & drop
 * - Select mail type per bureau (First Class, Certified, etc.)
 * - Preview the exact package that will be mailed
 * - Send via Lob.com integration
 * - CONFIRMATION REQUIRED before sending mail with cost preview
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import CursiveSignatureSelector from '../components/CursiveSignatureSelector';
import MobileNav from '../components/MobileNav';
import PrintMailPacketTab from './PrintMailPacketTab';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Mail, Upload, FileText, X, Send, Truck, CheckCircle2, Loader2, Package, Eye,
  GripVertical, ArrowUp, ArrowDown, Download, AlertTriangle, ChevronDown, ChevronUp,
  Home, Plus, Trash2, PenTool, User, MapPin, Calendar, CreditCard, ExternalLink, Copy, Check, DollarSign, AlertCircle
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Bureau configuration - Order: TransUnion, Equifax, Experian
const BUREAUS = [
  { 
    key: 'TRANSUNION', 
    name: 'TransUnion', 
    color: 'bg-green-600',
    borderColor: 'border-green-500',
    textColor: 'text-green-400',
    address: 'P.O. Box 2000, Chester, PA 19016-2000'
  },
  { 
    key: 'EQUIFAX', 
    name: 'Equifax', 
    color: 'bg-red-600',
    borderColor: 'border-red-500',
    textColor: 'text-red-400',
    address: 'P.O. Box 740256, Atlanta, GA 30374-0256'
  },
  { 
    key: 'EXPERIAN', 
    name: 'Experian', 
    color: 'bg-blue-600',
    borderColor: 'border-blue-500',
    textColor: 'text-blue-400',
    address: 'P.O. Box 4500, Allen, TX 75013'
  }
];

const mailTypeOptions = [
  { value: 'usps_first_class', label: 'First Class (3-5 days)', cost: '$1.50' },
  { value: 'certified', label: 'Certified Mail (5-7 days)', cost: '$8.50' },
  { value: 'certified_return_receipt', label: 'Certified + Return Receipt', cost: '$12.50' }
];

// US Letter size in points (8.5" x 11")
const LETTER_WIDTH = 612;
const LETTER_HEIGHT = 792;

/**
 * Merge multiple PDF documents and normalize all pages to US Letter size.
 * Lob.com requires all pages to have the same dimensions.
 * 
 * @param {Array} docs - Array of document objects with blob property
 * @returns {Promise<Blob>} - Merged PDF blob with all pages normalized to Letter size
 */
const mergePDFsWithNormalization = async (docs) => {
  const { PDFDocument } = await import('pdf-lib');
  const mergedPdf = await PDFDocument.create();
  
  for (const doc of docs) {
    if (doc.blob) {
      try {
        const docBytes = await doc.blob.arrayBuffer();
        const sourcePdf = await PDFDocument.load(docBytes);
        const pageCount = sourcePdf.getPageCount();
        
        for (let pageIndex = 0; pageIndex < pageCount; pageIndex++) {
          const sourcePage = sourcePdf.getPage(pageIndex);
          const { width: sourceWidth, height: sourceHeight } = sourcePage.getSize();
          
          // Check if page needs normalization (tolerance of 2 points)
          const needsNormalization = 
            Math.abs(sourceWidth - LETTER_WIDTH) > 2 || 
            Math.abs(sourceHeight - LETTER_HEIGHT) > 2;
          
          if (needsNormalization) {
            // Create a new Letter-sized page
            const newPage = mergedPdf.addPage([LETTER_WIDTH, LETTER_HEIGHT]);
            
            // Calculate scale to fit the source page content into Letter size
            // Maintain aspect ratio and center the content
            const scaleX = LETTER_WIDTH / sourceWidth;
            const scaleY = LETTER_HEIGHT / sourceHeight;
            const scale = Math.min(scaleX, scaleY, 1); // Don't scale up, only down
            
            const scaledWidth = sourceWidth * scale;
            const scaledHeight = sourceHeight * scale;
            
            // Center the content on the new page
            const offsetX = (LETTER_WIDTH - scaledWidth) / 2;
            const offsetY = (LETTER_HEIGHT - scaledHeight) / 2;
            
            // Embed the source page directly from source PDF and draw it
            const [embeddedPage] = await mergedPdf.embedPdf(sourcePdf, [pageIndex]);
            newPage.drawPage(embeddedPage, {
              x: offsetX,
              y: offsetY,
              width: scaledWidth,
              height: scaledHeight,
            });
            
            console.log(`[PDF Normalize] ${doc.fileName || 'Document'} Page ${pageIndex + 1}: ${Math.round(sourceWidth)}x${Math.round(sourceHeight)} → ${LETTER_WIDTH}x${LETTER_HEIGHT} (scale: ${scale.toFixed(2)})`);
          } else {
            // Page is already Letter size, copy it directly
            const [copiedPage] = await mergedPdf.copyPages(sourcePdf, [pageIndex]);
            mergedPdf.addPage(copiedPage);
            console.log(`[PDF Normalize] ${doc.fileName || 'Document'} Page ${pageIndex + 1}: Already Letter size, copied directly`);
          }
        }
      } catch (err) {
        console.error(`Error processing ${doc.fileName}:`, err);
        // Try fallback: just copy pages without normalization
        try {
          const docBytes = await doc.blob.arrayBuffer();
          const sourcePdf = await PDFDocument.load(docBytes);
          const pages = await mergedPdf.copyPages(sourcePdf, sourcePdf.getPageIndices());
          pages.forEach(page => mergedPdf.addPage(page));
          console.warn(`[PDF Normalize] ${doc.fileName}: Fallback - copied without normalization`);
        } catch (fallbackErr) {
          console.error(`[PDF Normalize] ${doc.fileName}: Complete failure - ${fallbackErr}`);
        }
      }
    }
  }
  
  const mergedBytes = await mergedPdf.save();
  return new Blob([mergedBytes], { type: 'application/pdf' });
};

// Create a blank page PDF blob for Lob.com
const createBlankPageBlob = async () => {
  const { PDFDocument, rgb, StandardFonts } = await import('pdf-lib');
  const pdfDoc = await PDFDocument.create();
  const page = pdfDoc.addPage([612, 792]);
  const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
  
  // Add visible label so user knows this is the Lob address page
  page.drawText('[ BLANK PAGE FOR LOB.COM ]', {
    x: 180,
    y: 420,
    size: 16,
    font: font,
    color: rgb(0.7, 0.7, 0.7),
  });
  page.drawText('Lob.com prints the mailing address here', {
    x: 175,
    y: 395,
    size: 12,
    font: font,
    color: rgb(0.7, 0.7, 0.7),
  });
  page.drawText('Your return address will appear in the corner', {
    x: 160,
    y: 375,
    size: 11,
    font: font,
    color: rgb(0.7, 0.7, 0.7),
  });
  
  const pdfBytes = await pdfDoc.save();
  return new Blob([pdfBytes], { type: 'application/pdf' });
};

// 2026-02-24 — All third-party mail providers removed at user request
// (Lob / PostGrid / LetterStream / Click2Mail all required verification
// hoops the user did not want to jump through).  The Just Mail flow now
// merges + downloads the packet — user prints, drops at USPS with
// Certified Mail postage, and pastes the tracking number back into the
// Mail History table.  Chip below just points to USPS's Click-N-Ship so
// the user can print postage at home if they want.
const PostGridStatusChip = () => (
  <a href="https://cns.usps.com/labelInformation.shtml" target="_blank" rel="noopener noreferrer"
     className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-blue-500/20 to-indigo-500/20 border border-blue-500/40 rounded-xl hover:from-blue-500/30 hover:to-indigo-500/30 transition"
     data-testid="usps-chip">
    <div className="p-2 bg-blue-500/20 rounded-lg">
      <Mail className="w-5 h-5 text-blue-400" />
    </div>
    <div className="text-left">
      <p className="text-blue-300 font-semibold text-sm">USPS Click-N-Ship (optional)</p>
      <p className="text-[11px] text-zinc-400 underline">Print postage at home →</p>
    </div>
    <ExternalLink className="w-3.5 h-3.5 text-blue-400 ml-1" />
  </a>
);

export default function JustMailPage() {
  const navigate = useNavigate();

  // ═══════════════════════════════════════════════════════════════════
  // TAB STATE - Switch between "Send Mail" and "Mail History"
  // ═══════════════════════════════════════════════════════════════════
  const [activeTab, setActiveTab] = useState('packet'); // 'packet', 'send', 'history'
  
  // ═══════════════════════════════════════════════════════════════════
  // MAIL HISTORY STATE
  // ═══════════════════════════════════════════════════════════════════
  const [mailHistory, setMailHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [reMailingId, setReMailingId] = useState(null);
  const [refreshingTracking, setRefreshingTracking] = useState(false);

  // 2026-02-24 — All third-party mail providers removed; there's no
  // provider to auto-refresh from.  The user pastes their USPS tracking
  // number manually via the Mail History row (see backend endpoint
  // POST /api/mail/{id}/manual-tracking).  Kept the state/handler shape
  // so nothing breaks even though no button uses it right now.
  const refreshTracking = async () => {
    toast.info('Tracking is manual now — paste your USPS number on each row in Mail History.');
  };
  
  // Resend Modal State
  const [resendModal, setResendModal] = useState({ open: false, mail: null });
  const [resendBureaus, setResendBureaus] = useState({ EXPERIAN: false, EQUIFAX: false, TRANSUNION: false });
  const [resending, setResending] = useState(false);
  
  // Fetch mail history
  const fetchMailHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(`${API}/api/mail/history/all`);
      setMailHistory(response.data.mail_records || []);
    } catch (err) {
      console.error('Error fetching mail history:', err);
      // Try localStorage fallback
      const cached = localStorage.getItem('just_mail_history');
      if (cached) {
        setMailHistory(JSON.parse(cached));
      }
    }
    setLoadingHistory(false);
  }, []);
  
  // Load history when tab changes
  React.useEffect(() => {
    if (activeTab === 'history') {
      fetchMailHistory();
    }
  }, [activeTab, fetchMailHistory]);
  
  // ═══════════════════════════════════════════════════════════════════
  // CUSTOM ADDRESS MODE - For sending to addresses other than bureaus
  // ═══════════════════════════════════════════════════════════════════
  const [mailMode, setMailMode] = useState('bureaus'); // 'bureaus' or 'custom'
  const [customRecipient, setCustomRecipient] = useState({
    name: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    zip: ''
  });
  const [customReturnAddress, setCustomReturnAddress] = useState({
    name: '',
    address_line1: '',
    city: '',
    state: '',
    zip: ''
  });
  const [customRecipientPaste, setCustomRecipientPaste] = useState('');
  const [customReturnPaste, setCustomReturnPaste] = useState('');
  const [customDocuments, setCustomDocuments] = useState([]);
  const [customMailType, setCustomMailType] = useState('usps_first_class');
  const [sendingCustom, setSendingCustom] = useState(false);
  
  // Parse custom recipient address from pasted text
  const parseCustomRecipient = () => {
    const lines = customRecipientPaste.split('\n').map(l => l.trim()).filter(l => l);
    if (lines.length < 3) {
      toast.error('Please paste at least 3 lines: Name, Address, City State Zip');
      return;
    }
    
    // Parse: Name, Address, City State Zip (or City, State, Zip on separate lines)
    const parsed = {
      name: lines[0] || '',
      address_line1: lines[1] || '',
      address_line2: '',
      city: '',
      state: '',
      zip: ''
    };
    
    // Check if line 3 is a combined City State Zip
    const line3 = lines[2] || '';
    const combinedPattern = /^(.+?)[,\s]+([A-Z]{2})\s+(\d{5}(-\d{4})?)$/i;
    const match = line3.match(combinedPattern);
    
    if (match) {
      parsed.city = match[1].trim();
      parsed.state = match[2].toUpperCase();
      parsed.zip = match[3];
      if (lines[3]) parsed.address_line2 = ''; // No address line 2
    } else if (lines.length >= 5) {
      // Separate City, State, Zip lines
      parsed.city = lines[2] || '';
      parsed.state = (lines[3] || '').toUpperCase();
      parsed.zip = lines[4] || '';
    } else {
      // Try parsing line 3 as "City, State Zip" or "City State Zip"
      const parts = line3.split(/[,\s]+/);
      if (parts.length >= 3) {
        parsed.zip = parts.pop();
        parsed.state = parts.pop().toUpperCase();
        parsed.city = parts.join(' ');
      }
    }
    
    setCustomRecipient(parsed);
    toast.success('Recipient address parsed!');
  };
  
  // Parse custom return address from pasted text
  const parseCustomReturnAddress = () => {
    const lines = customReturnPaste.split('\n').map(l => l.trim()).filter(l => l);
    if (lines.length < 3) {
      toast.error('Please paste at least 3 lines: Name, Address, City State Zip');
      return;
    }
    
    const parsed = {
      name: lines[0] || '',
      address_line1: lines[1] || '',
      city: '',
      state: '',
      zip: ''
    };
    
    const line3 = lines[2] || '';
    const combinedPattern = /^(.+?)[,\s]+([A-Z]{2})\s+(\d{5}(-\d{4})?)$/i;
    const match = line3.match(combinedPattern);
    
    if (match) {
      parsed.city = match[1].trim();
      parsed.state = match[2].toUpperCase();
      parsed.zip = match[3];
    } else {
      const parts = line3.split(/[,\s]+/);
      if (parts.length >= 3) {
        parsed.zip = parts.pop();
        parsed.state = parts.pop().toUpperCase();
        parsed.city = parts.join(' ');
      }
    }
    
    setCustomReturnAddress(parsed);
    toast.success('Return address parsed!');
  };
  
  // Initialize custom documents with blank page
  React.useEffect(() => {
    const initCustomBlankPage = async () => {
      if (customDocuments.length === 0) {
        try {
          const blankBlob = await createBlankPageBlob();
          setCustomDocuments([{
            id: 'blank-page-CUSTOM',
            fileName: '📄 BLANK PAGE (Lob Address)',
            blob: blankBlob,
            docType: 'BLANK_PAGE',
            isBlankPage: true,
            addedAt: new Date().toISOString()
          }]);
        } catch (err) {
          console.error('Error creating blank page for custom:', err);
        }
      }
    };
    if (mailMode === 'custom') {
      initCustomBlankPage();
    }
  }, [mailMode, customDocuments.length]);
  
  // Document states for each bureau - will be initialized with blank page
  const [documents, setDocuments] = useState({
    EXPERIAN: [],
    EQUIFAX: [],
    TRANSUNION: []
  });
  
  // Track if blank pages have been initialized
  const [blankPagesInitialized, setBlankPagesInitialized] = useState(false);
  
  // Initialize blank pages on mount
  React.useEffect(() => {
    const initializeBlankPages = async () => {
      if (blankPagesInitialized) return;
      
      try {
        const blankBlob = await createBlankPageBlob();
        
        setDocuments({
          EXPERIAN: [{
            id: 'blank-page-EXP',
            fileName: '📄 BLANK PAGE (Lob Address)',
            blob: blankBlob,
            docType: 'BLANK_PAGE',
            isBlankPage: true,
            addedAt: new Date().toISOString()
          }],
          EQUIFAX: [{
            id: 'blank-page-EQU',
            fileName: '📄 BLANK PAGE (Lob Address)',
            blob: blankBlob,
            docType: 'BLANK_PAGE',
            isBlankPage: true,
            addedAt: new Date().toISOString()
          }],
          TRANSUNION: [{
            id: 'blank-page-TU',
            fileName: '📄 BLANK PAGE (Lob Address)',
            blob: blankBlob,
            docType: 'BLANK_PAGE',
            isBlankPage: true,
            addedAt: new Date().toISOString()
          }]
        });
        
        setBlankPagesInitialized(true);
      } catch (err) {
        console.error('Error creating blank pages:', err);
      }
    };
    
    initializeBlankPages();
  }, [blankPagesInitialized]);
  
  // Expanded/collapsed state for bureau sections
  const [expandedBureaus, setExpandedBureaus] = useState({
    EXPERIAN: true,
    EQUIFAX: true,
    TRANSUNION: true
  });
  
  // Drag state
  const [draggedDoc, setDraggedDoc] = useState(null);
  const [draggedBureau, setDraggedBureau] = useState(null);
  const [dragOverBureau, setDragOverBureau] = useState(null);
  
  // Mail type per bureau - Default to First Class
  const [bureauMailTypes, setBureauMailTypes] = useState({
    EXPERIAN: 'usps_first_class',
    EQUIFAX: 'usps_first_class',
    TRANSUNION: 'usps_first_class'
  });
  
  // Sending states
  const [sending, setSending] = useState({
    EXPERIAN: false,
    EQUIFAX: false,
    TRANSUNION: false
  });
  
  const [mailResults, setMailResults] = useState({
    EXPERIAN: null,
    EQUIFAX: null,
    TRANSUNION: null
  });
  
  const [sendingAll, setSendingAll] = useState(false);
  
  // Preview/Download loading
  const [previewLoading, setPreviewLoading] = useState({
    EXPERIAN: false,
    EQUIFAX: false,
    TRANSUNION: false
  });
  
  const [downloadLoading, setDownloadLoading] = useState({
    EXPERIAN: false,
    EQUIFAX: false,
    TRANSUNION: false
  });
  
  // ═══════════════════════════════════════════════════════════════════
  // CLIENT INFO & DOCUMENT GENERATION STATE
  // ═══════════════════════════════════════════════════════════════════
  const [declarationExpanded, setDeclarationExpanded] = useState(true);
  const [clientInfo, setClientInfo] = useState({
    name: '',
    dob: '',
    ssn: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    email: ''
  });
  const [pasteText, setPasteText] = useState('');
  const [generatingDeclaration, setGeneratingDeclaration] = useState(false);
  const [generatingCoverLetter, setGeneratingCoverLetter] = useState(false);
  
  // Signature state - uses CursiveSignatureSelector component
  const [selectedSignatureStyleId, setSelectedSignatureStyleId] = useState('cursive_elegant');
  const [signatureImageData, setSignatureImageData] = useState(null);
  
  // Legacy signature style (kept for backward compatibility)
  const [signatureStyle, setSignatureStyle] = useState(1);
  
  // Experian upload modal state
  const [showExperianUploadModal, setShowExperianUploadModal] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  
  // Bureau selection checkboxes
  const [selectedBureaus, setSelectedBureaus] = useState({
    EXPERIAN: false,
    EQUIFAX: false,
    TRANSUNION: false
  });
  
  // Toggle bureau selection
  const toggleBureauSelection = (bureau) => {
    setSelectedBureaus(prev => ({ ...prev, [bureau]: !prev[bureau] }));
  };
  
  // Get list of selected bureaus
  const getSelectedBureausList = () => {
    return Object.entries(selectedBureaus)
      .filter(([_, selected]) => selected)
      .map(([bureau]) => bureau);
  };
  
  // ═══════════════════════════════════════════════════════════════════
  // EXPERIAN UPLOAD HELPER FUNCTIONS
  // ═══════════════════════════════════════════════════════════════════
  
  // Copy text to clipboard
  const copyToClipboard = async (text, fieldName) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      toast.success(`${fieldName} copied!`);
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };
  
  // Open Experian upload page and show helper modal
  const openExperianUpload = () => {
    setShowExperianUploadModal(true);
    // Open the Experian upload page in a new tab
    window.open('https://www.experian.com/consumer/upload/', '_blank');
  };
  
  // Parse name into first and last name
  const getNameParts = () => {
    const nameParts = (clientInfo.name || '').trim().split(/\s+/);
    return {
      firstName: nameParts[0] || '',
      middleInitial: nameParts.length > 2 ? nameParts[1]?.charAt(0) || '' : '',
      lastName: nameParts.length > 2 ? nameParts.slice(2).join(' ') : nameParts[1] || ''
    };
  };
  
  // Format name for cursive signature display (Title Case - first letter capital only)
  const formatNameForSignature = (name) => {
    if (!name) return 'John Doe';
    return name
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };
  
  // ═══════════════════════════════════════════════════════════════════
  // PARSE CLIENT INFO - SMART FORMAT DETECTION
  // Supports TWO formats:
  // 
  // FORMAT A (7 lines - City/State/Zip combined):
  // Line 1: Name, Line 2: DOB, Line 3: SSN, Line 4: Address, 
  // Line 5: City State Zip, Line 6: Phone, Line 7: Email
  //
  // FORMAT B (9 lines - City/State/Zip separate):
  // Line 1: Name, Line 2: DOB, Line 3: SSN, Line 4: Address, 
  // Line 5: City, Line 6: State, Line 7: Zip, Line 8: Phone, Line 9: Email
  // ═══════════════════════════════════════════════════════════════════
  const parseClientInfo = (text) => {
    const lines = text.split('\n').map(l => l.trim()).filter(l => l);
    
    // Always: Name, DOB, SSN, Address are first 4 lines
    const parsed = {
      name: lines[0] || '',
      dob: lines[1] || '',
      ssn: lines[2] || '',
      address: lines[3] || '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      email: ''
    };
    
    if (lines.length >= 5) {
      const line5 = lines[4] || '';
      
      // Check if line 5 looks like a combined "CITY STATE ZIP" format
      // Pattern: "SAVANNAH GA 31419" or "Los Angeles, CA 90001"
      const combinedPattern = /^(.+?)[,\s]+([A-Z]{2})\s+(\d{5}(-\d{4})?)$/i;
      const match = line5.match(combinedPattern);
      
      if (match && lines.length <= 7) {
        // FORMAT A: 7-line format with combined City State Zip
        parsed.city = match[1].trim();
        parsed.state = match[2].toUpperCase();
        parsed.zip = match[3];
        parsed.phone = lines[5] || '';
        parsed.email = lines[6] || '';
      } else if (lines.length >= 9) {
        // FORMAT B: 9-line format with separate City, State, Zip
        parsed.city = lines[4] || '';
        parsed.state = (lines[5] || '').toUpperCase();
        parsed.zip = lines[6] || '';
        parsed.phone = lines[7] || '';
        parsed.email = lines[8] || '';
      } else {
        // Fallback: Try to detect which format based on line content
        // Check if line 5 contains numbers (likely zip code = 9-line format)
        // or if line 6 looks like a phone number
        const line6 = lines[5] || '';
        const isLine6Phone = /^[\d\(\)\-\.\s]+$/.test(line6) && line6.replace(/\D/g, '').length >= 10;
        const isLine6Email = line6.includes('@');
        
        if (isLine6Phone || isLine6Email) {
          // Likely 7-line format but combined pattern didn't match
          // Try splitting line 5 by spaces from the end
          const words = line5.split(/\s+/);
          if (words.length >= 2) {
            const lastWord = words[words.length - 1];
            const secondLast = words[words.length - 2];
            
            // Check if last word is zip and second last is state
            if (/^\d{5}(-\d{4})?$/.test(lastWord) && /^[A-Z]{2}$/i.test(secondLast)) {
              parsed.zip = lastWord;
              parsed.state = secondLast.toUpperCase();
              parsed.city = words.slice(0, -2).join(' ');
            } else {
              // Just use line 5 as city
              parsed.city = line5;
            }
          } else {
            parsed.city = line5;
          }
          parsed.phone = isLine6Phone ? line6 : '';
          parsed.email = isLine6Email ? line6 : (lines[6] || '');
          if (!parsed.phone && lines[6] && !lines[6].includes('@')) {
            parsed.phone = lines[6];
          }
        } else {
          // Default to 9-line parsing
          parsed.city = lines[4] || '';
          parsed.state = (lines[5] || '').toUpperCase();
          parsed.zip = lines[6] || '';
          parsed.phone = lines[7] || '';
          parsed.email = lines[8] || '';
        }
      }
    }
    
    return parsed;
  };
  
  // Parse button click handler
  const handleParseClick = () => {
    if (!pasteText.trim()) {
      toast.error('Please paste client info first');
      return;
    }
    const parsed = parseClientInfo(pasteText);
    setClientInfo(parsed);
    
    const lineCount = pasteText.split('\n').map(l => l.trim()).filter(l => l).length;
    
    if (parsed.name) {
      toast.success(`Parsed ${lineCount}-line format for: ${parsed.name}`);
    } else {
      toast.error('Could not parse info. Make sure each field is on its own line.');
    }
  };
  
  // ═══════════════════════════════════════════════════════════════════
  // DRAW CURSIVE SIGNATURE - 3 styles to choose from
  // ═══════════════════════════════════════════════════════════════════
  const drawCursiveSignature = (page, name, x, y, style, pdfDoc) => {
    // Style 1: Elegant script with flourish
    // Style 2: Bold flowing signature
    // Style 3: Simple cursive
    
    const { rgb } = require('pdf-lib');
    
    // All styles use dark blue ink color
    const inkColor = rgb(0, 0, 0);  // BLACK ink for signature
    
    if (style === 1) {
      // Style 1: Elegant with underline flourish
      page.drawText(name, {
        x, y,
        size: 16,
        color: inkColor,
      });
      // Underline flourish
      page.drawLine({
        start: { x, y: y - 3 },
        end: { x: x + name.length * 8, y: y - 3 },
        thickness: 0.8,
        color: inkColor,
      });
      // Small flourish at end
      page.drawLine({
        start: { x: x + name.length * 8, y: y - 3 },
        end: { x: x + name.length * 8 + 15, y: y + 5 },
        thickness: 0.5,
        color: inkColor,
      });
    } else if (style === 2) {
      // Style 2: Bold with double underline
      page.drawText(name, {
        x, y,
        size: 18,
        color: inkColor,
      });
      // Double underline
      page.drawLine({
        start: { x, y: y - 4 },
        end: { x: x + name.length * 9, y: y - 4 },
        thickness: 1.2,
        color: inkColor,
      });
      page.drawLine({
        start: { x: x + 10, y: y - 8 },
        end: { x: x + name.length * 9 - 10, y: y - 8 },
        thickness: 0.5,
        color: inkColor,
      });
    } else {
      // Style 3: Simple clean signature
      page.drawText(name, {
        x, y,
        size: 15,
        color: inkColor,
      });
      // Simple underline
      page.drawLine({
        start: { x, y: y - 2 },
        end: { x: x + name.length * 7.5, y: y - 2 },
        thickness: 0.5,
        color: inkColor,
      });
    }
  };
  
  // ═══════════════════════════════════════════════════════════════════
  // GENERATE COVER LETTER - SIMPLIFIED
  // Only: "ATTENTION DISPUTE DEPARTMENT" + Client Personal Info + Signature
  // NO explanation text - the Declaration handles that
  // ═══════════════════════════════════════════════════════════════════
  const generateCoverLetter = async () => {
    if (!clientInfo.name) {
      toast.error('Client name is required');
      return;
    }
    
    const bureausList = getSelectedBureausList();
    if (bureausList.length === 0) {
      toast.error('Please select at least one bureau');
      return;
    }
    
    setGeneratingCoverLetter(true);
    
    try {
      const { PDFDocument, rgb, StandardFonts } = await import('pdf-lib');
      const pdfDoc = await PDFDocument.create();
      const page = pdfDoc.addPage([612, 792]); // Letter size
      
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      const italicFont = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);
      
      const { width, height } = page.getSize();
      let y = height - 80;
      const leftMargin = 72;
      const centerX = width / 2;
      const lineHeight = 24;
      
      // ═══════════════════════════════════════════════════════════════════
      // BIG BOLD HEADER: ATTENTION DISPUTE DEPARTMENT (centered, large)
      // ═══════════════════════════════════════════════════════════════════
      const headerText = 'ATTENTION DISPUTE DEPARTMENT';
      const headerWidth = boldFont.widthOfTextAtSize(headerText, 26);
      page.drawText(headerText, {
        x: centerX - headerWidth / 2,
        y,
        size: 26,
        font: boldFont,
        color: rgb(0, 0, 0),
      });
      y -= 60;
      
      // ═══════════════════════════════════════════════════════════════════
      // CONSUMER PERSONAL INFORMATION - Each field on its own line
      // ═══════════════════════════════════════════════════════════════════
      page.drawText('CONSUMER PERSONAL INFORMATION', {
        x: leftMargin,
        y,
        size: 14,
        font: boldFont,
        color: rgb(0.1, 0.1, 0.3),
      });
      y -= 8;
      
      // Draw a separator line
      page.drawLine({
        start: { x: leftMargin, y },
        end: { x: width - leftMargin, y },
        thickness: 1,
        color: rgb(0.3, 0.3, 0.5),
      });
      y -= 28;
      
      // Full Legal Name
      page.drawText('Full Legal Name:', { x: leftMargin, y, size: 12, font: boldFont, color: rgb(0.2, 0.2, 0.2) });
      page.drawText(formatNameForSignature(clientInfo.name), { x: leftMargin + 140, y, size: 12, font: italicFont, color: rgb(0, 0, 0) });
      y -= lineHeight;
      
      // Date of Birth
      page.drawText('Date of Birth:', { x: leftMargin, y, size: 12, font: boldFont, color: rgb(0.2, 0.2, 0.2) });
      page.drawText(clientInfo.dob || 'N/A', { x: leftMargin + 140, y, size: 12, font: italicFont, color: rgb(0, 0, 0) });
      y -= lineHeight;
      
      // Social Security Number
      page.drawText('Social Security #:', { x: leftMargin, y, size: 12, font: boldFont, color: rgb(0.2, 0.2, 0.2) });
      page.drawText(clientInfo.ssn || 'N/A', { x: leftMargin + 140, y, size: 12, font: italicFont, color: rgb(0, 0, 0) });
      y -= lineHeight;
      
      // Street Address
      page.drawText('Street Address:', { x: leftMargin, y, size: 12, font: boldFont, color: rgb(0.2, 0.2, 0.2) });
      page.drawText(clientInfo.address || 'N/A', { x: leftMargin + 140, y, size: 12, font: italicFont, color: rgb(0, 0, 0) });
      y -= lineHeight;
      
      // City, State, Zip
      page.drawText('City, State, Zip:', { x: leftMargin, y, size: 12, font: boldFont, color: rgb(0.2, 0.2, 0.2) });
      const cityStateZipText = `${clientInfo.city || ''}, ${clientInfo.state || ''} ${clientInfo.zip || ''}`.trim();
      page.drawText(cityStateZipText || 'N/A', { x: leftMargin + 140, y, size: 12, font: italicFont, color: rgb(0, 0, 0) });
      y -= lineHeight;
      
      // Phone Number - SEPARATE LINE
      page.drawText('Phone Number:', { x: leftMargin, y, size: 12, font: boldFont, color: rgb(0.2, 0.2, 0.2) });
      page.drawText(clientInfo.phone || 'N/A', { x: leftMargin + 140, y, size: 12, font: italicFont, color: rgb(0, 0, 0) });
      y -= lineHeight;
      
      // Email Address - SEPARATE LINE
      page.drawText('Email Address:', { x: leftMargin, y, size: 12, font: boldFont, color: rgb(0.2, 0.2, 0.2) });
      page.drawText(clientInfo.email || 'N/A', { x: leftMargin + 140, y, size: 12, font: italicFont, color: rgb(0, 0, 0) });
      y -= 50;
      
      // ═══════════════════════════════════════════════════════════════════
      // CURSIVE SIGNATURE IMAGE - Using CursiveSignatureSelector image
      // ═══════════════════════════════════════════════════════════════════
      if (signatureImageData && signatureImageData.startsWith('data:image')) {
        // Embed actual signature image from CursiveSignatureSelector
        try {
          const signatureBase64 = signatureImageData.split(',')[1];
          const signatureBytes = Uint8Array.from(atob(signatureBase64), c => c.charCodeAt(0));
          const signatureImage = await pdfDoc.embedPng(signatureBytes);
          
          // Scale signature to fit - max width 200, max height 60
          const sigDims = signatureImage.scale(1);
          const maxWidth = 200;
          const maxHeight = 60;
          const scale = Math.min(maxWidth / sigDims.width, maxHeight / sigDims.height, 1);
          
          page.drawImage(signatureImage, {
            x: leftMargin,
            y: y - (sigDims.height * scale) + 20,
            width: sigDims.width * scale,
            height: sigDims.height * scale,
          });
        } catch (sigError) {
          console.warn('Failed to embed signature image, falling back to text:', sigError);
          // Fallback to text if image embedding fails
          const inkColor = rgb(0, 0, 0);
          const signatureName = formatNameForSignature(clientInfo.name);
          page.drawText(signatureName, { x: leftMargin, y, size: 22, font: italicFont, color: inkColor });
        }
      } else {
        // Fallback: Draw text signature (legacy behavior)
        const inkColor = rgb(0, 0, 0);
        const signatureName = formatNameForSignature(clientInfo.name);
        
        if (signatureStyle === 1) {
          page.drawText(signatureName, { x: leftMargin, y, size: 22, font: italicFont, color: inkColor });
          const sigWidth = signatureName.length * 11;
          page.drawLine({ start: { x: leftMargin, y: y - 5 }, end: { x: leftMargin + sigWidth, y: y - 5 }, thickness: 1, color: inkColor });
          page.drawLine({ start: { x: leftMargin + sigWidth, y: y - 5 }, end: { x: leftMargin + sigWidth + 25, y: y + 10 }, thickness: 0.7, color: inkColor });
        } else if (signatureStyle === 2) {
          page.drawText(signatureName, { x: leftMargin, y, size: 24, font: italicFont, color: inkColor });
          const sigWidth = signatureName.length * 12;
          page.drawLine({ start: { x: leftMargin, y: y - 6 }, end: { x: leftMargin + sigWidth, y: y - 6 }, thickness: 2, color: inkColor });
          page.drawLine({ start: { x: leftMargin + 15, y: y - 12 }, end: { x: leftMargin + sigWidth - 15, y: y - 12 }, thickness: 0.7, color: inkColor });
        } else {
          page.drawText(signatureName, { x: leftMargin, y, size: 20, font: italicFont, color: inkColor });
          const sigWidth = signatureName.length * 10;
          page.drawLine({ start: { x: leftMargin, y: y - 4 }, end: { x: leftMargin + sigWidth, y: y - 4 }, thickness: 0.6, color: inkColor });
        }
      }
      
      // Generate PDF
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      
      // Download the PDF
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Cover_Letter_${clientInfo.name.replace(/\s+/g, '_')}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      
      // Add to selected bureau folders
      const coverLetterDoc = {
        id: `cover-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        fileName: `Cover_Letter_${clientInfo.name.replace(/\s+/g, '_')}.pdf`,
        blob: blob,
        docType: 'COVER_LETTER',
        addedAt: new Date().toISOString()
      };
      
      setDocuments(prev => {
        const updated = { ...prev };
        bureausList.forEach(bureau => {
          updated[bureau] = [...prev[bureau], { ...coverLetterDoc, id: `${coverLetterDoc.id}-${bureau}` }];
        });
        return updated;
      });
      
      toast.success(`Cover Letter downloaded and added to ${bureausList.join(', ')}!`);
      
    } catch (error) {
      console.error('Error generating cover letter:', error);
      toast.error('Failed to generate cover letter');
    } finally {
      setGeneratingCoverLetter(false);
    }
  };
  
  // ═══════════════════════════════════════════════════════════════════
  // GENERATE HANDWRITTEN DECLARATION - Universal for all bureaus
  // Includes: Return address, Name, DOB, SSN, Address
  // EXCLUDES: Phone and Email (per user request)
  // ═══════════════════════════════════════════════════════════════════
  const generateDeclaration = async () => {
    if (!clientInfo.name) {
      toast.error('Client name is required');
      return;
    }
    
    const bureausList = getSelectedBureausList();
    if (bureausList.length === 0) {
      toast.error('Please select at least one bureau');
      return;
    }
    
    setGeneratingDeclaration(true);
    
    try {
      const { PDFDocument, rgb, StandardFonts } = await import('pdf-lib');
      const pdfDoc = await PDFDocument.create();
      const page = pdfDoc.addPage([612, 792]);
      
      const font = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const boldFont = await pdfDoc.embedFont(StandardFonts.HelveticaBold);
      const italicFont = await pdfDoc.embedFont(StandardFonts.HelveticaOblique);
      
      const { width, height } = page.getSize();
      let y = height - 50;
      const leftMargin = 72;
      
      // Return Address (top left) - NO phone/email
      page.drawText(clientInfo.name.toUpperCase(), {
        x: leftMargin,
        y: height - 40,
        size: 10,
        font: font,
        color: rgb(0.2, 0.2, 0.2),
      });
      if (clientInfo.address) {
        page.drawText(clientInfo.address.toUpperCase(), {
          x: leftMargin,
          y: height - 52,
          size: 10,
          font: font,
          color: rgb(0.2, 0.2, 0.2),
        });
      }
      const cityStateZip = `${clientInfo.city}, ${clientInfo.state} ${clientInfo.zip}`.toUpperCase();
      page.drawText(cityStateZip, {
        x: leftMargin,
        y: height - 64,
        size: 10,
        font: font,
        color: rgb(0.2, 0.2, 0.2),
      });
      
      y = height - 100;
      
      // Title
      const titleText = 'DECLARATION OF IDENTITY AND SELF-REPRESENTATION';
      const titleWidth = boldFont.widthOfTextAtSize(titleText, 14);
      page.drawText(titleText, {
        x: (width - titleWidth) / 2,
        y,
        size: 14,
        font: boldFont,
        color: rgb(0.1, 0.1, 0.2),
      });
      y -= 20;
      
      const subtitleText = 'Under Penalty of Perjury';
      const subtitleWidth = font.widthOfTextAtSize(subtitleText, 10);
      page.drawText(subtitleText, {
        x: (width - subtitleWidth) / 2,
        y,
        size: 10,
        font: font,
        color: rgb(0.4, 0.4, 0.4),
      });
      y -= 35;
      
      page.drawText(`I, ${clientInfo.name.toUpperCase()}, hereby declare under penalty of perjury that:`, {
        x: leftMargin,
        y,
        size: 11,
        font: italicFont,
        color: rgb(0.1, 0.1, 0.2),
      });
      y -= 26;
      
      const points = [
        '1. I am the individual named above and I am submitting this dispute on my own behalf.',
        '',
        '2. I am NOT represented by a third-party credit repair organization, company, or agent',
        '   in this matter.',
        '',
        '3. All information provided in this dispute package is true and accurate to the best',
        '   of my knowledge.',
        '',
        '4. I am exercising my rights under the Fair Credit Reporting Act (FCRA),',
        '   15 U.S.C. § 1681 et seq.',
        '',
        '5. I have personally reviewed and signed all documents contained in this package.',
      ];
      
      for (const line of points) {
        page.drawText(line, {
          x: leftMargin,
          y,
          size: 11,
          font: italicFont,
          color: rgb(0.1, 0.1, 0.2),
        });
        y -= 15;
      }
      y -= 12;
      
      // Consumer Info Box - NO phone/email
      page.drawText('CONSUMER IDENTIFYING INFORMATION:', {
        x: leftMargin,
        y,
        size: 11,
        font: boldFont,
        color: rgb(0.2, 0.2, 0.2),
      });
      y -= 8;
      
      page.drawRectangle({
        x: leftMargin - 5,
        y: y - 70,
        width: width - leftMargin * 2 + 10,
        height: 75,
        borderColor: rgb(0.8, 0.8, 0.8),
        borderWidth: 1,
      });
      y -= 18;
      
      page.drawText(`Full Legal Name: ${clientInfo.name.toUpperCase()}`, {
        x: leftMargin + 5, y, size: 11, font: italicFont, color: rgb(0.1, 0.1, 0.2),
      });
      y -= 17;
      
      page.drawText(`Date of Birth: ${clientInfo.dob || 'N/A'}`, {
        x: leftMargin + 5, y, size: 11, font: italicFont, color: rgb(0.1, 0.1, 0.2),
      });
      y -= 17;
      
      const fullAddress = `${clientInfo.address}, ${clientInfo.city}, ${clientInfo.state} ${clientInfo.zip}`;
      page.drawText(`Current Address: ${fullAddress}`, {
        x: leftMargin + 5, y, size: 11, font: italicFont, color: rgb(0.1, 0.1, 0.2),
      });
      y -= 17;
      
      page.drawText(`Social Security Number: ${clientInfo.ssn || '___-__-____'}`, {
        x: leftMargin + 5, y, size: 11, font: boldFont, color: rgb(0.8, 0.2, 0.2),
      });
      y -= 40;
      
      // Closing
      page.drawText('I declare under penalty of perjury under the laws of the United States of America', {
        x: leftMargin, y, size: 11, font: italicFont, color: rgb(0.1, 0.1, 0.2),
      });
      y -= 15;
      page.drawText('that the foregoing is true and correct.', {
        x: leftMargin, y, size: 11, font: italicFont, color: rgb(0.1, 0.1, 0.2),
      });
      y -= 30;
      
      const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
      page.drawText(`Executed on: ${today}`, {
        x: leftMargin, y, size: 11, font: italicFont, color: rgb(0.1, 0.1, 0.2),
      });
      y -= 40;
      
      // ═══════════════════════════════════════════════════════════════════
      // CURSIVE SIGNATURE IMAGE - Using CursiveSignatureSelector image
      // ═══════════════════════════════════════════════════════════════════
      if (signatureImageData && signatureImageData.startsWith('data:image')) {
        // Embed actual signature image from CursiveSignatureSelector
        try {
          const signatureBase64 = signatureImageData.split(',')[1];
          const signatureBytes = Uint8Array.from(atob(signatureBase64), c => c.charCodeAt(0));
          const signatureImage = await pdfDoc.embedPng(signatureBytes);
          
          // Scale signature to fit - max width 200, max height 60
          const sigDims = signatureImage.scale(1);
          const maxWidth = 200;
          const maxHeight = 60;
          const scale = Math.min(maxWidth / sigDims.width, maxHeight / sigDims.height, 1);
          
          page.drawImage(signatureImage, {
            x: leftMargin,
            y: y - (sigDims.height * scale) + 20,
            width: sigDims.width * scale,
            height: sigDims.height * scale,
          });
        } catch (sigError) {
          console.warn('Failed to embed signature image, falling back to text:', sigError);
          // Fallback to text if image embedding fails
          const inkColor = rgb(0, 0, 0);
          const signatureName = formatNameForSignature(clientInfo.name);
          page.drawText(signatureName, { x: leftMargin, y, size: 22, font: italicFont, color: inkColor });
        }
      } else {
        // Fallback: Draw text signature (legacy behavior)
        const inkColor = rgb(0, 0, 0);
        const signatureName = formatNameForSignature(clientInfo.name);
        
        if (signatureStyle === 1) {
          page.drawText(signatureName, { x: leftMargin, y, size: 22, font: italicFont, color: inkColor });
          const sigWidth = signatureName.length * 11;
          page.drawLine({ start: { x: leftMargin, y: y - 5 }, end: { x: leftMargin + sigWidth, y: y - 5 }, thickness: 1, color: inkColor });
          page.drawLine({ start: { x: leftMargin + sigWidth, y: y - 5 }, end: { x: leftMargin + sigWidth + 25, y: y + 10 }, thickness: 0.7, color: inkColor });
        } else if (signatureStyle === 2) {
          page.drawText(signatureName, { x: leftMargin, y, size: 24, font: italicFont, color: inkColor });
          const sigWidth = signatureName.length * 12;
          page.drawLine({ start: { x: leftMargin, y: y - 6 }, end: { x: leftMargin + sigWidth, y: y - 6 }, thickness: 2, color: inkColor });
          page.drawLine({ start: { x: leftMargin + 15, y: y - 12 }, end: { x: leftMargin + sigWidth - 15, y: y - 12 }, thickness: 0.7, color: inkColor });
        } else {
          page.drawText(signatureName, { x: leftMargin, y, size: 20, font: italicFont, color: inkColor });
          const sigWidth = signatureName.length * 10;
          page.drawLine({ start: { x: leftMargin, y: y - 4 }, end: { x: leftMargin + sigWidth, y: y - 4 }, thickness: 0.6, color: inkColor });
        }
      }
      
      // Generate PDF
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      
      // Download
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Handwritten_Declaration_${clientInfo.name.replace(/\s+/g, '_')}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      
      // Add to selected bureau folders
      const declarationDoc = {
        id: `decl-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        fileName: `Handwritten_Declaration_${clientInfo.name.replace(/\s+/g, '_')}.pdf`,
        blob: blob,
        docType: 'HANDWRITTEN_DECLARATION',
        addedAt: new Date().toISOString()
      };
      
      setDocuments(prev => {
        const updated = { ...prev };
        bureausList.forEach(bureau => {
          updated[bureau] = [...prev[bureau], { ...declarationDoc, id: `${declarationDoc.id}-${bureau}` }];
        });
        return updated;
      });
      
      toast.success(`Declaration downloaded and added to ${bureausList.join(', ')}!`);
      
    } catch (error) {
      console.error('Error generating declaration:', error);
      toast.error('Failed to generate declaration');
    } finally {
      setGeneratingDeclaration(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // FILE UPLOAD HANDLERS
  // ═══════════════════════════════════════════════════════════════════
  
  const handleFileUpload = (bureau) => async (e) => {
    const files = Array.from(e.target.files || []);
    const pdfFiles = files.filter(f => f.type === 'application/pdf' || f.name.endsWith('.pdf'));
    
    if (pdfFiles.length === 0) {
      toast.error('Please select PDF files only');
      return;
    }
    
    // Convert files to our document format with blobs
    const newDocs = await Promise.all(pdfFiles.map(async (file, index) => {
      const blob = new Blob([await file.arrayBuffer()], { type: 'application/pdf' });
      return {
        id: `${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
        fileName: file.name,
        blob: blob,
        addedAt: new Date().toISOString()
      };
    }));
    
    setDocuments(prev => ({
      ...prev,
      [bureau]: [...prev[bureau], ...newDocs]
    }));
    
    toast.success(`${pdfFiles.length} file(s) added to ${bureau}`);
    e.target.value = '';
  };
  
  const handleDrop = (bureau) => async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverBureau(null);
    
    const files = Array.from(e.dataTransfer.files);
    const pdfFiles = files.filter(f => f.type === 'application/pdf' || f.name.endsWith('.pdf'));
    
    if (pdfFiles.length === 0) {
      toast.error('Please drop PDF files only');
      return;
    }
    
    const newDocs = await Promise.all(pdfFiles.map(async (file, index) => {
      const blob = new Blob([await file.arrayBuffer()], { type: 'application/pdf' });
      return {
        id: `${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`,
        fileName: file.name,
        blob: blob,
        addedAt: new Date().toISOString()
      };
    }));
    
    setDocuments(prev => ({
      ...prev,
      [bureau]: [...prev[bureau], ...newDocs]
    }));
    
    toast.success(`${pdfFiles.length} file(s) added to ${bureau}`);
  };
  
  const handleDragOver = (bureau) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverBureau(bureau);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverBureau(null);
  };

  // ═══════════════════════════════════════════════════════════════════
  // DOCUMENT REORDERING
  // ═══════════════════════════════════════════════════════════════════
  
  const moveDocumentUp = (bureau, index) => {
    if (index <= 0) return;
    setDocuments(prev => {
      const docs = [...prev[bureau]];
      [docs[index - 1], docs[index]] = [docs[index], docs[index - 1]];
      return { ...prev, [bureau]: docs };
    });
  };
  
  const moveDocumentDown = (bureau, index) => {
    setDocuments(prev => {
      const docs = [...prev[bureau]];
      if (index >= docs.length - 1) return prev;
      [docs[index], docs[index + 1]] = [docs[index + 1], docs[index]];
      return { ...prev, [bureau]: docs };
    });
  };
  
  const removeDocument = (bureau, docId) => {
    setDocuments(prev => ({
      ...prev,
      [bureau]: prev[bureau].filter(d => d.id !== docId)
    }));
    toast.info('Document removed');
  };
  
  // Drag & drop within document list
  const handleDocDragStart = (bureau, doc, index) => (e) => {
    setDraggedDoc({ doc, index });
    setDraggedBureau(bureau);
    e.dataTransfer.effectAllowed = 'move';
  };
  
  const handleDocDragOver = (bureau, index) => (e) => {
    e.preventDefault();
    if (draggedBureau !== bureau) return;
    e.dataTransfer.dropEffect = 'move';
  };
  
  const handleDocDrop = (bureau, dropIndex) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!draggedDoc || draggedBureau !== bureau) return;
    
    const { index: dragIndex } = draggedDoc;
    if (dragIndex === dropIndex) return;
    
    setDocuments(prev => {
      const docs = [...prev[bureau]];
      const [removed] = docs.splice(dragIndex, 1);
      docs.splice(dropIndex, 0, removed);
      return { ...prev, [bureau]: docs };
    });
    
    setDraggedDoc(null);
    setDraggedBureau(null);
  };
  
  const handleDocDragEnd = () => {
    setDraggedDoc(null);
    setDraggedBureau(null);
  };
  
  // Toggle bureau expansion
  const toggleBureauExpand = (bureau) => {
    setExpandedBureaus(prev => ({ ...prev, [bureau]: !prev[bureau] }));
  };
  
  // Preview single document
  const previewDocument = (doc) => {
    if (doc.blob) {
      const url = URL.createObjectURL(doc.blob);
      window.open(url, '_blank');
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // PREVIEW & DOWNLOAD - Merges all documents with page size normalization
  // All pages normalized to US Letter (612x792) for Lob.com compatibility
  // ═══════════════════════════════════════════════════════════════════
  
  const previewPackage = async (bureau) => {
    const docs = documents[bureau];
    if (docs.length === 0) {
      toast.error(`No documents in ${bureau} package`);
      return;
    }
    
    setPreviewLoading(prev => ({ ...prev, [bureau]: true }));
    
    try {
      // Use normalized merge to ensure all pages are same size
      const blob = await mergePDFsWithNormalization(docs);
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      toast.success(`Preview opened for ${bureau} - ${docs.length} document(s), all pages normalized to Letter size!`);
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to generate preview');
    } finally {
      setPreviewLoading(prev => ({ ...prev, [bureau]: false }));
    }
  };
  
  const downloadPackage = async (bureau) => {
    const docs = documents[bureau];
    if (docs.length === 0) {
      toast.error(`No documents in ${bureau} package`);
      return;
    }
    
    setDownloadLoading(prev => ({ ...prev, [bureau]: true }));
    
    try {
      // Use normalized merge to ensure all pages are same size
      const blob = await mergePDFsWithNormalization(docs);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${bureau}_Mail_Package_${new Date().toISOString().split('T')[0]}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${bureau} package (all pages normalized to Letter size)!`);
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download package');
    } finally {
      setDownloadLoading(prev => ({ ...prev, [bureau]: false }));
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // MAIL CONFIRMATION STATE
  // ═══════════════════════════════════════════════════════════════════
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [pendingMailBureau, setPendingMailBureau] = useState(null);
  const [costEstimate, setCostEstimate] = useState(null);
  const [fetchingEstimate, setFetchingEstimate] = useState(false);
  
  // Calculate cost estimate for Just Mail (uses document count and pages)
  const calculateCostEstimate = (bureau) => {
    const docs = documents[bureau];
    const mailType = bureauMailTypes[bureau] || 'certified';
    
    // Estimate pages (1 page per document + blank page for Lob)
    const pageCount = docs.length + 1;
    
    // LOB.COM PRICING (as of 2024):
    // First Class: $0.63 first page + $0.08 each additional page
    // Certified: $4.50 base + $0.63 first page + $0.08 each additional page  
    // Certified with Return Receipt: $8.00 base + $0.63 first page + $0.08 each additional page
    let estimatedCost;
    if (mailType === 'usps_first_class') {
      estimatedCost = 0.63 + (0.08 * (pageCount - 1));
    } else if (mailType === 'certified') {
      estimatedCost = 4.50 + 0.63 + (0.08 * (pageCount - 1));
    } else if (mailType === 'certified_return_receipt') {
      estimatedCost = 8.00 + 0.63 + (0.08 * (pageCount - 1));
    } else {
      estimatedCost = 4.50 + 0.63 + (0.08 * (pageCount - 1));
    }
    
    return {
      bureau: bureau,
      client_name: clientInfo.name,
      mail_type: mailType,
      document_count: docs.length,
      page_count: pageCount,
      estimated_cost: Math.round(estimatedCost * 100) / 100,
      cost_breakdown: {
        base_fee: mailType.includes('return') ? 8.00 : (mailType === 'certified' ? 4.50 : 0.63),
        per_page_fee: 0.08,
        pages: pageCount
      }
    };
  };
  
  // Show confirmation dialog before sending mail
  const handleSendMailClick = (bureau) => {
    const docs = documents[bureau];
    if (docs.length === 0) {
      toast.error(`No documents to send for ${bureau}`);
      return;
    }
    
    // Validate return address
    if (!clientInfo.name || !clientInfo.address || !clientInfo.city || !clientInfo.state || !clientInfo.zip) {
      toast.error('Please fill in your client info (name, address, city, state, zip) at the top before sending');
      return;
    }
    
    // Calculate and show cost estimate
    const estimate = calculateCostEstimate(bureau);
    setCostEstimate(estimate);
    setPendingMailBureau(bureau);
    setConfirmDialogOpen(true);
  };
  
  // Confirmed - actually send the mail
  const confirmAndSendMail = async () => {
    if (!pendingMailBureau) return;
    
    const bureau = pendingMailBureau;
    setConfirmDialogOpen(false);
    setCostEstimate(null);
    setPendingMailBureau(null);
    
    await sendMailViaLob(bureau, true);  // Pass confirmed=true
  };

  // ═══════════════════════════════════════════════════════════════════
  // SEND VIA LOB.COM
  // Merges documents with page normalization for Lob.com compatibility
  // Uses client info as return address
  // REQUIRES CONFIRMATION before actually sending
  // ═══════════════════════════════════════════════════════════════════
  
  const sendMailViaLob = async (bureau, confirmed = false) => {
    const docs = documents[bureau];
    if (docs.length === 0) {
      toast.error(`No documents to send for ${bureau}`);
      return;
    }
    
    // Validate return address
    if (!clientInfo.name || !clientInfo.address || !clientInfo.city || !clientInfo.state || !clientInfo.zip) {
      toast.error('Please fill in your client info (name, address, city, state, zip) at the top before sending');
      return;
    }
    
    // SAFETY CHECK: Require explicit confirmation
    if (!confirmed) {
      handleSendMailClick(bureau);
      return;
    }
    
    setSending(prev => ({ ...prev, [bureau]: true }));
    
    try {
      // Merge all PDFs with page size normalization (Lob requires all pages same dimension)
      toast.info('Preparing documents... normalizing page sizes for Lob.com');
      const mergedBlob = await mergePDFsWithNormalization(docs);
      
      // Create form data with return address from client info
      const formData = new FormData();
      formData.append('bureau', bureau.toLowerCase());
      formData.append('mail_type', bureauMailTypes[bureau]);
      formData.append('documents', mergedBlob, `${bureau}_Package.pdf`);
      
      // Add return address from client info
      if (clientInfo.name) {
        formData.append('return_name', clientInfo.name);
      }
      if (clientInfo.address) {
        formData.append('return_address', clientInfo.address);
      }
      if (clientInfo.city) {
        formData.append('return_city', clientInfo.city);
      }
      if (clientInfo.state) {
        formData.append('return_state', clientInfo.state);
      }
      if (clientInfo.zip) {
        formData.append('return_zip', clientInfo.zip);
      }
      
      const response = await axios.post(`${API}/api/mail/send-uploaded-files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Check if mail was actually sent successfully
      if (response.data.success && response.data.lob_id) {
        setMailResults(prev => ({
          ...prev,
          [bureau]: {
            success: true,
            data: response.data,
            sentAt: new Date().toISOString()
          }
        }));
        toast.success(`✅ Mail sent to ${bureau}! Lob ID: ${response.data.lob_id}`);
        
        // Refresh mail history
        fetchMailHistory();
      } else if (response.data.status === 'lob_error') {
        // Lob API returned an error
        const errorMsg = response.data.error || response.data.message || 'Lob API error - mail was not sent';
        setMailResults(prev => ({
          ...prev,
          [bureau]: {
            success: false,
            error: errorMsg
          }
        }));
        toast.error(`❌ Failed to send to ${bureau}: ${errorMsg}`);
      } else if (response.data.status === 'sent_mock') {
        // Mock mode - Lob not configured
        setMailResults(prev => ({
          ...prev,
          [bureau]: {
            success: false,
            error: 'Lob.com not configured - mail was not actually sent'
          }
        }));
        toast.warning(`⚠️ Mock mode: ${bureau} mail was NOT actually sent. Configure Lob.com API key.`);
      } else {
        // Unknown status
        setMailResults(prev => ({
          ...prev,
          [bureau]: {
            success: false,
            error: response.data.message || 'Unknown error'
          }
        }));
        toast.error(`❌ ${bureau}: ${response.data.message || 'Mail may not have been sent'}`);
      }
    } catch (error) {
      console.error('Mail error:', error);
      setMailResults(prev => ({
        ...prev,
        [bureau]: {
          success: false,
          error: error.response?.data?.detail || error.message
        }
      }));
      toast.error(`Failed to send to ${bureau}: ${error.response?.data?.detail || error.message}`);
    } finally {
      setSending(prev => ({ ...prev, [bureau]: false }));
    }
  };
  
  // State for batch mail confirmation
  const [batchConfirmOpen, setBatchConfirmOpen] = useState(false);
  const [batchEstimates, setBatchEstimates] = useState([]);
  
  // Show batch confirmation for sending all bureaus
  const handleSendAllClick = () => {
    const bureausWithDocs = BUREAUS.filter(b => 
      documents[b.key].length > 0 && !mailResults[b.key]?.success
    );
    
    if (bureausWithDocs.length === 0) {
      toast.error('No packages ready to send');
      return;
    }
    
    // Validate return address
    if (!clientInfo.name || !clientInfo.address || !clientInfo.city || !clientInfo.state || !clientInfo.zip) {
      toast.error('Please fill in your client info (name, address, city, state, zip) at the top before sending');
      return;
    }
    
    // Calculate estimates for all bureaus
    const estimates = bureausWithDocs.map(b => calculateCostEstimate(b.key));
    setBatchEstimates(estimates);
    setBatchConfirmOpen(true);
  };
  
  // Confirm and send all bureaus
  const confirmAndSendAllBureaus = async () => {
    setBatchConfirmOpen(false);
    setSendingAll(true);
    
    let successCount = 0;
    let totalCost = 0;
    
    for (const estimate of batchEstimates) {
      try {
        await sendMailViaLob(estimate.bureau, true);  // confirmed=true
        successCount++;
        totalCost += estimate.estimated_cost || 0;
      } catch (err) {
        console.error(`Failed to send ${estimate.bureau}:`, err);
      }
    }
    
    setSendingAll(false);
    setBatchEstimates([]);
    
    if (successCount > 0) {
      toast.success(`🎉 Successfully mailed ${successCount} package(s)! Total cost: ~$${totalCost.toFixed(2)}`);
    }
  };
  
  const sendAllBureaus = async () => {
    // Route through confirmation dialog
    handleSendAllClick();
  };

  // ═══════════════════════════════════════════════════════════════════
  // HELPERS
  // ═══════════════════════════════════════════════════════════════════
  
  const getTotalDocs = () => {
    return Object.values(documents).reduce((sum, docs) => sum + docs.length, 0);
  };
  
  const getReadyPacketsCount = () => {
    return BUREAUS.filter(b => documents[b.key].length > 0 && !mailResults[b.key]?.success).length;
  };
  
  const getTotalCostEstimate = () => {
    // Use new accurate Lob pricing
    let total = 0;
    BUREAUS.forEach(bureau => {
      if (documents[bureau.key].length > 0 && !mailResults[bureau.key]?.success) {
        const estimate = calculateCostEstimate(bureau.key);
        total += estimate.estimated_cost;
      }
    });
    return total.toFixed(2);
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-6" data-testid="just-mail-page">
      {/* Mobile Navigation Menu */}
      <MobileNav />
      
      {/* Header */}
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="text-zinc-400 hover:text-white"
            >
              <Home className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
          </div>
          <Badge variant="outline" className="text-lg px-4 py-1 border-purple-500 text-purple-400">
            <Mail className="w-4 h-4 mr-2" />
            Print & Mail
          </Badge>
        </div>

        <Card className="bg-zinc-900 border-zinc-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-purple-500/20 rounded-xl">
                  <Mail className="w-8 h-8 text-purple-400" />
                </div>
                <div>
                  <CardTitle className="text-2xl text-white">Print & Mail</CardTitle>
                  <p className="text-zinc-400 mt-1">
                    Pick a client, presets auto-load, print per bureau. USPS integration coming — printing works today.
                  </p>
                </div>
              </div>
              {/* 2026-02-24 — PostGrid account chip.  PostGrid does not
                  expose the wallet balance in their public API (only the
                  cumulative spend), so we surface: total spent + live-
                  approval status + a deep-link to the dashboard where the
                  real balance lives.  If liveApproved is false we render
                  a red warning so the user knows real mail won't send yet. */}
              <PostGridStatusChip />
            </div>
          </CardHeader>
        </Card>

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* TABS - Print & Mail Packet | Send New Mail (legacy) | History */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <div className="flex gap-2 border-b border-zinc-700 pb-2 flex-wrap">
          <Button
            variant={activeTab === 'packet' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('packet')}
            className={activeTab === 'packet' ? 'bg-purple-600 hover:bg-purple-700' : ''}
            data-testid="tab-packet"
          >
            🖨️ Print & Mail Packet
          </Button>
          <Button
            variant={activeTab === 'send' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('send')}
            className={activeTab === 'send' ? 'bg-purple-600 hover:bg-purple-700' : ''}
            data-testid="tab-send"
          >
            <Send className="w-4 h-4 mr-2" />
            Custom Mail (advanced)
          </Button>
          <Button
            variant={activeTab === 'history' ? 'default' : 'ghost'}
            onClick={() => setActiveTab('history')}
            className={activeTab === 'history' ? 'bg-purple-600 hover:bg-purple-700' : ''}
            data-testid="tab-history"
          >
            <Package className="w-4 h-4 mr-2" />
            Mail History
          </Button>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* PRINT & MAIL PACKET TAB — client picker + presets + bureau prints */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'packet' && (
          <PrintMailPacketTab />
        )}

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* MAIL HISTORY TAB */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'history' && (
          <Card className="bg-zinc-900 border-zinc-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-white flex items-center gap-2">
                  <Package className="w-5 h-5 text-purple-400" />
                  Mail History
                </CardTitle>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={fetchMailHistory} disabled={loadingHistory}>
                    {loadingHistory ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loadingHistory ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
                </div>
              ) : mailHistory.length === 0 ? (
                <div className="text-center py-8 text-zinc-400">
                  <Mail className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No mail history yet</p>
                  <p className="text-sm">Mail sent through Print & Mail will appear here</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {mailHistory.map((mail, idx) => (
                    <div key={mail.mail_id || idx} className="p-4 bg-zinc-800 rounded-lg border border-zinc-700">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          {/* Status Badge and Lob ID */}
                          <div className="flex items-center flex-wrap gap-2 mb-3">
                            {/* Send Status */}
                            <Badge 
                              variant={mail.status === 'sent' ? 'default' : mail.status === 'lob_error' ? 'destructive' : 'secondary'} 
                              className={mail.status === 'sent' ? 'bg-green-600' : mail.status === 'lob_error' ? 'bg-red-600' : ''}
                            >
                              {mail.status || 'unknown'}
                            </Badge>
                            {/* Tracking Status - from webhook updates */}
                            {mail.tracking_status && (
                              <Badge 
                                className={
                                  mail.tracking_status === 'delivered' ? 'bg-emerald-600' :
                                  mail.tracking_status === 'in_transit' ? 'bg-blue-600' :
                                  mail.tracking_status === 'in_local_area' ? 'bg-purple-600' :
                                  mail.tracking_status === 'processed_for_delivery' ? 'bg-orange-600' :
                                  mail.tracking_status === 'returned_to_sender' ? 'bg-red-600' :
                                  'bg-zinc-600'
                                }
                              >
                                {mail.tracking_status === 'in_transit' ? '🚚 In Transit' :
                                 mail.tracking_status === 'delivered' ? '✅ Delivered' :
                                 mail.tracking_status === 'in_local_area' ? '📍 In Local Area' :
                                 mail.tracking_status === 'processed_for_delivery' ? '📦 Out for Delivery' :
                                 mail.tracking_status === 'returned_to_sender' ? '⚠️ Returned' :
                                 mail.tracking_status.replace(/_/g, ' ')}
                              </Badge>
                            )}
                            {mail.lob_id && (
                              <span className="text-xs text-green-400 font-mono bg-green-500/10 px-2 py-0.5 rounded">{mail.lob_id}</span>
                            )}
                            {mail.mail_type && (
                              <span className="text-xs text-zinc-500 bg-zinc-700 px-2 py-0.5 rounded">{mail.mail_type.replace(/_/g, ' ')}</span>
                            )}
                            {/* Source indicator */}
                            {mail.source && (
                              <span className="text-xs text-cyan-400 bg-cyan-500/10 px-2 py-0.5 rounded">
                                {mail.source === 'dispute_wizard' ? '📋 Dispute Wizard' : 
                                 mail.source === 'just_mail_bureau' ? '🏛️ Bureau Mail' :
                                 mail.source === 'just_mail_custom' ? '✉️ Custom Mail' :
                                 mail.source}
                              </span>
                            )}
                          </div>
                          
                          {/* TO: Recipient Info */}
                          <div className="mb-2">
                            <span className="text-xs text-purple-400 font-semibold uppercase tracking-wide">To:</span>
                            <p className="text-white font-medium">{mail.recipient_name || mail.bureau || 'Unknown Recipient'}</p>
                            <p className="text-sm text-zinc-400">{mail.recipient_address || mail.address || ''}</p>
                          </div>
                          
                          {/* FROM: Sender Info */}
                          {mail.sender_name && (
                            <div className="mb-2">
                              <span className="text-xs text-blue-400 font-semibold uppercase tracking-wide">From:</span>
                              <p className="text-zinc-300 text-sm">{mail.sender_name}</p>
                              {mail.return_address && (
                                <p className="text-xs text-zinc-500">
                                  {typeof mail.return_address === 'object' 
                                    ? `${mail.return_address.address_line1 || ''}, ${mail.return_address.address_city || ''}, ${mail.return_address.address_state || ''} ${mail.return_address.address_zip || ''}`
                                    : mail.return_address
                                  }
                                </p>
                              )}
                            </div>
                          )}
                          
                          {mail.error && (
                            <p className="text-sm text-red-400 mt-1">Error: {mail.error}</p>
                          )}
                          
                          {/* Timeline: Sent → Expected/Delivered */}
                          <div className="mt-3 p-2 bg-zinc-900 rounded-lg border border-zinc-700">
                            <div className="flex items-center gap-2 text-xs">
                              {/* Sent Date */}
                              <div className="flex items-center gap-1">
                                <div className="w-2 h-2 rounded-full bg-green-500"></div>
                                <span className="text-zinc-400">Sent:</span>
                                <span className="text-white">{new Date(mail.created_at || mail.sent_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                              </div>
                              
                              {/* Arrow */}
                              <span className="text-zinc-600">→</span>
                              
                              {/* Expected/Delivered */}
                              {mail.tracking_status === 'delivered' ? (
                                <div className="flex items-center gap-1">
                                  <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                                  <span className="text-emerald-400">Delivered:</span>
                                  <span className="text-emerald-300">
                                    {mail.last_tracking_update 
                                      ? new Date(mail.last_tracking_update).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                      : 'Confirmed'}
                                  </span>
                                </div>
                              ) : mail.expected_delivery ? (
                                <div className="flex items-center gap-1">
                                  <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse"></div>
                                  <span className="text-zinc-400">ETA:</span>
                                  <span className="text-yellow-400">{new Date(mail.expected_delivery).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1">
                                  <div className="w-2 h-2 rounded-full bg-zinc-500"></div>
                                  <span className="text-zinc-500">ETA: Pending</span>
                                </div>
                              )}
                              
                              {/* Last Update */}
                              {mail.last_tracking_update && mail.tracking_status !== 'delivered' && (
                                <>
                                  <span className="text-zinc-600">|</span>
                                  <span className="text-zinc-500">
                                    Updated: {new Date(mail.last_tracking_update).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                          
                          {/* Additional Info Row */}
                          <div className="flex items-center gap-4 mt-2 text-xs text-zinc-500">
                            {mail.tracking_number && <span><Truck className="w-3 h-3 inline mr-1" />{mail.tracking_number}</span>}
                          </div>
                        </div>
                        {/* Right Side: Cost + Action Buttons */}
                        <div className="flex flex-col items-end gap-2">
                          {/* Cost Badge - Prominent */}
                          {(mail.cost !== undefined && mail.cost !== null) && (
                            <div className="flex items-center gap-1 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                              <DollarSign className="w-4 h-4 text-emerald-400" />
                              <span className="text-emerald-400 font-bold text-lg">
                                {typeof mail.cost === 'number' ? mail.cost.toFixed(2) : parseFloat(mail.cost || 0).toFixed(2)}
                              </span>
                            </div>
                          )}
                          {/* 2026-02-24 — Self-mail buttons.
                              • Download PDF   → grab the merged packet
                              • Paste Tracking → save the USPS tracking number
                                                 after mailing */}
                          {mail.download_url && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(`${API}${mail.download_url}`, '_blank')}
                              className="border-emerald-500 text-emerald-400 hover:bg-emerald-500/20"
                              data-testid={`mail-download-${mail.id}`}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Download PDF
                            </Button>
                          )}
                          {mail.status !== 'mailed' && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={async () => {
                                const tn = window.prompt(`Paste USPS Certified tracking number for this ${mail.bureau || 'bureau'} letter (looks like 9407…):`, mail.tracking_number || '');
                                if (!tn || tn.trim().length < 10) return;
                                try {
                                  await axios.post(`${API}/api/mail/${mail.id}/manual-tracking`, { tracking_number: tn.trim() });
                                  toast.success('Tracking saved — click the tracking number to open USPS');
                                  fetchMailHistory();
                                } catch (e) {
                                  toast.error(e.response?.data?.detail || 'Failed to save tracking');
                                }
                              }}
                              className="border-amber-500 text-amber-400 hover:bg-amber-500/20"
                              data-testid={`mail-paste-tracking-${mail.id}`}
                            >
                              <Truck className="w-4 h-4 mr-1" />
                              {mail.tracking_number ? 'Update Tracking' : 'Paste Tracking #'}
                            </Button>
                          )}
                          {mail.usps_tracking_url && (
                            <a href={mail.usps_tracking_url} target="_blank" rel="noopener noreferrer"
                               className="inline-flex items-center gap-1 px-2 py-1 rounded border border-blue-500 text-blue-400 hover:bg-blue-500/20 text-xs font-medium"
                               data-testid={`mail-usps-track-${mail.id}`}>
                              <ExternalLink className="w-3.5 h-3.5" /> Track on USPS
                            </a>
                          )}
                          {/* Preview Button */}
                          {mail.lob_url && (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => window.open(mail.lob_url, '_blank')}
                              className="border-blue-500 text-blue-400 hover:bg-blue-500/20"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Preview
                            </Button>
                          )}
                          {/* Resend Button - Opens Modal with Bureau Selection */}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setResendModal({ open: true, mail });
                              // Pre-select the bureau that was originally sent to
                              const originalBureau = (mail.bureau || mail.recipient_name || '').toUpperCase();
                              setResendBureaus({
                                EXPERIAN: originalBureau.includes('EXPERIAN'),
                                EQUIFAX: originalBureau.includes('EQUIFAX'),
                                TRANSUNION: originalBureau.includes('TRANSUNION') || originalBureau.includes('TRANS')
                              });
                            }}
                            className="border-purple-500 text-purple-400 hover:bg-purple-500/20"
                          >
                            <Send className="w-4 h-4 mr-1" />
                            Resend
                          </Button>
                          {/* Delete Button */}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={async () => {
                              if (window.confirm('Are you sure you want to delete this mail record?')) {
                                try {
                                  const mailId = mail.mail_id || mail.id;
                                  await axios.delete(`${API}/api/mail/history/${mailId}`);
                                  toast.success('Mail record deleted');
                                  fetchMailHistory();
                                } catch (err) {
                                  toast.error('Failed to delete: ' + (err.response?.data?.detail || err.message));
                                }
                              }
                            }}
                            className="border-red-500 text-red-400 hover:bg-red-500/20"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}
          
        {/* Resend Modal with Bureau Selection */}
        {resendModal.open && resendModal.mail && (
            <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
              <Card className="bg-zinc-900 border-purple-500 max-w-md w-full">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-purple-500/20 rounded-lg">
                        <Send className="w-5 h-5 text-purple-400" />
                      </div>
                      <div>
                        <CardTitle className="text-lg text-white">Resend Mail</CardTitle>
                        <p className="text-sm text-zinc-400">Select bureaus to send to</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setResendModal({ open: false, mail: null })}
                      className="text-zinc-400 hover:text-white"
                    >
                      <X className="w-5 h-5" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Original Mail Info */}
                  <div className="p-3 bg-zinc-800 rounded-lg">
                    <p className="text-sm text-zinc-400">Original: {resendModal.mail.recipient_name || resendModal.mail.bureau}</p>
                    <p className="text-xs text-zinc-500">{resendModal.mail.recipient_address || ''}</p>
                  </div>
                  
                  {/* Bureau Selection */}
                  <div className="space-y-3">
                    <Label className="text-white">Select Bureaus to Send To:</Label>
                    
                    <div className="space-y-2">
                      {/* Select All */}
                      <label className="flex items-center gap-3 p-2 bg-zinc-800 rounded cursor-pointer hover:bg-zinc-700">
                        <input
                          type="checkbox"
                          checked={resendBureaus.EXPERIAN && resendBureaus.EQUIFAX && resendBureaus.TRANSUNION}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setResendBureaus({ EXPERIAN: checked, EQUIFAX: checked, TRANSUNION: checked });
                          }}
                          className="w-4 h-4 rounded"
                        />
                        <span className="text-white font-medium">Select All 3 Bureaus</span>
                      </label>
                      
                      {/* Individual Bureaus */}
                      <label className="flex items-center gap-3 p-2 bg-zinc-800 rounded cursor-pointer hover:bg-zinc-700">
                        <input
                          type="checkbox"
                          checked={resendBureaus.TRANSUNION}
                          onChange={(e) => setResendBureaus(prev => ({ ...prev, TRANSUNION: e.target.checked }))}
                          className="w-4 h-4 rounded"
                        />
                        <Badge className="bg-green-600">TransUnion</Badge>
                        <span className="text-xs text-zinc-400">P.O. Box 2000, Chester, PA 19016</span>
                      </label>
                      
                      <label className="flex items-center gap-3 p-2 bg-zinc-800 rounded cursor-pointer hover:bg-zinc-700">
                        <input
                          type="checkbox"
                          checked={resendBureaus.EQUIFAX}
                          onChange={(e) => setResendBureaus(prev => ({ ...prev, EQUIFAX: e.target.checked }))}
                          className="w-4 h-4 rounded"
                        />
                        <Badge className="bg-red-600">Equifax</Badge>
                        <span className="text-xs text-zinc-400">P.O. Box 740256, Atlanta, GA 30374</span>
                      </label>
                      
                      <label className="flex items-center gap-3 p-2 bg-zinc-800 rounded cursor-pointer hover:bg-zinc-700">
                        <input
                          type="checkbox"
                          checked={resendBureaus.EXPERIAN}
                          onChange={(e) => setResendBureaus(prev => ({ ...prev, EXPERIAN: e.target.checked }))}
                          className="w-4 h-4 rounded"
                        />
                        <Badge className="bg-blue-600">Experian</Badge>
                        <span className="text-xs text-zinc-400">P.O. Box 4500, Allen, TX 75013</span>
                      </label>
                    </div>
                  </div>
                  
                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-2">
                    <Button
                      variant="outline"
                      className="flex-1"
                      onClick={() => setResendModal({ open: false, mail: null })}
                    >
                      Cancel
                    </Button>
                    <Button
                      className="flex-1 bg-purple-600 hover:bg-purple-700"
                      disabled={resending || (!resendBureaus.EXPERIAN && !resendBureaus.EQUIFAX && !resendBureaus.TRANSUNION)}
                      onClick={async () => {
                        setResending(true);
                        const selectedBureauKeys = Object.entries(resendBureaus)
                          .filter(([_, selected]) => selected)
                          .map(([key]) => key);
                        
                        // Go to Send tab with bureaus mode
                        setActiveTab('send');
                        setMailMode('bureaus');
                        
                        // Select the chosen bureaus
                        setSelectedBureaus(resendBureaus);
                        
                        // Close modal
                        setResendModal({ open: false, mail: null });
                        setResending(false);
                        
                        toast.success(`Selected ${selectedBureauKeys.length} bureau(s) - now upload your documents and click Send`);
                      }}
                    >
                      {resending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                      Continue to Send ({Object.values(resendBureaus).filter(v => v).length} bureau{Object.values(resendBureaus).filter(v => v).length !== 1 ? 's' : ''})
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* SEND MAIL TAB */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        {activeTab === 'send' && (
          <>
            {/* Mail Mode Toggle - Bureaus vs Custom Address */}
            <Card className="bg-zinc-900 border-zinc-700">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <Label className="text-white">Send To:</Label>
                  <div className="flex gap-2">
                    <Button
                      variant={mailMode === 'bureaus' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setMailMode('bureaus')}
                      className={mailMode === 'bureaus' ? 'bg-blue-600' : ''}
                    >
                      <CreditCard className="w-4 h-4 mr-2" />
                      Credit Bureaus
                    </Button>
                    <Button
                      variant={mailMode === 'custom' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setMailMode('custom')}
                      className={mailMode === 'custom' ? 'bg-green-600' : ''}
                    >
                      <MapPin className="w-4 h-4 mr-2" />
                      Custom Address
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Custom Address Section */}
            {mailMode === 'custom' && (
              <Card className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-green-400" />
                    Custom Recipient Address
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Paste Address */}
                  <div>
                    <Label className="text-zinc-300">Paste Recipient Address</Label>
                    <Textarea
                      value={customRecipientPaste}
                      onChange={(e) => setCustomRecipientPaste(e.target.value)}
                      placeholder="Paste address here:&#10;John Smith&#10;123 Main Street&#10;Los Angeles, CA 90001"
                      className="h-24 bg-zinc-800 border-zinc-600 mt-1"
                    />
                    <Button 
                      size="sm" 
                      className="mt-2 bg-green-600 hover:bg-green-700"
                      onClick={parseCustomRecipient}
                    >
                      Parse Address
                    </Button>
                  </div>
                  
                  {/* Parsed Fields */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-zinc-300">Recipient Name</Label>
                      <Input
                        value={customRecipient.name}
                        onChange={(e) => setCustomRecipient({...customRecipient, name: e.target.value})}
                        className="bg-zinc-800 border-zinc-600"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-300">Address Line 1</Label>
                      <Input
                        value={customRecipient.address_line1}
                        onChange={(e) => setCustomRecipient({...customRecipient, address_line1: e.target.value})}
                        className="bg-zinc-800 border-zinc-600"
                      />
                    </div>
                    <div>
                      <Label className="text-zinc-300">City</Label>
                      <Input
                        value={customRecipient.city}
                        onChange={(e) => setCustomRecipient({...customRecipient, city: e.target.value})}
                        className="bg-zinc-800 border-zinc-600"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-zinc-300">State</Label>
                        <Input
                          value={customRecipient.state}
                          onChange={(e) => setCustomRecipient({...customRecipient, state: e.target.value.toUpperCase()})}
                          className="bg-zinc-800 border-zinc-600"
                          maxLength={2}
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-300">ZIP</Label>
                        <Input
                          value={customRecipient.zip}
                          onChange={(e) => setCustomRecipient({...customRecipient, zip: e.target.value})}
                          className="bg-zinc-800 border-zinc-600"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Return Address Section */}
                  <div className="mt-6 pt-6 border-t border-green-500/30">
                    <h4 className="text-white font-medium flex items-center gap-2 mb-4">
                      <MapPin className="w-4 h-4 text-amber-400" />
                      Your Return Address
                    </h4>
                    
                    {/* Paste Return Address */}
                    <div className="mb-4">
                      <Label className="text-zinc-300">Paste Your Address</Label>
                      <Textarea
                        value={customReturnPaste}
                        onChange={(e) => setCustomReturnPaste(e.target.value)}
                        placeholder="Paste your address here:&#10;Your Name&#10;456 Your Street&#10;Your City, ST 12345"
                        className="h-20 bg-zinc-800 border-zinc-600 mt-1"
                      />
                      <Button 
                        size="sm" 
                        className="mt-2 bg-amber-600 hover:bg-amber-700"
                        onClick={parseCustomReturnAddress}
                      >
                        Parse Return Address
                      </Button>
                    </div>
                    
                    {/* Return Address Fields */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-zinc-300">Your Name</Label>
                        <Input
                          value={customReturnAddress.name}
                          onChange={(e) => setCustomReturnAddress({...customReturnAddress, name: e.target.value})}
                          className="bg-zinc-800 border-zinc-600"
                          placeholder="John Doe"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-300">Your Address</Label>
                        <Input
                          value={customReturnAddress.address_line1}
                          onChange={(e) => setCustomReturnAddress({...customReturnAddress, address_line1: e.target.value})}
                          className="bg-zinc-800 border-zinc-600"
                          placeholder="123 Main St"
                        />
                      </div>
                      <div>
                        <Label className="text-zinc-300">City</Label>
                        <Input
                          value={customReturnAddress.city}
                          onChange={(e) => setCustomReturnAddress({...customReturnAddress, city: e.target.value})}
                          className="bg-zinc-800 border-zinc-600"
                          placeholder="Los Angeles"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-zinc-300">State</Label>
                          <Input
                            value={customReturnAddress.state}
                            onChange={(e) => setCustomReturnAddress({...customReturnAddress, state: e.target.value.toUpperCase()})}
                            className="bg-zinc-800 border-zinc-600"
                            maxLength={2}
                            placeholder="CA"
                          />
                        </div>
                        <div>
                          <Label className="text-zinc-300">ZIP</Label>
                          <Input
                            value={customReturnAddress.zip}
                            onChange={(e) => setCustomReturnAddress({...customReturnAddress, zip: e.target.value})}
                            className="bg-zinc-800 border-zinc-600"
                            placeholder="90001"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Custom Documents Upload */}
                  <div className="mt-4 p-4 bg-zinc-800 rounded-lg border border-dashed border-zinc-600">
                    <div className="flex items-center justify-between mb-3">
                      <Label className="text-white">Documents to Mail</Label>
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept=".pdf"
                          multiple
                          className="hidden"
                          onChange={async (e) => {
                            const files = Array.from(e.target.files || []);
                            for (const file of files) {
                              if (file.type === 'application/pdf') {
                                const blob = new Blob([await file.arrayBuffer()], { type: 'application/pdf' });
                                setCustomDocuments(prev => [...prev, {
                                  id: `custom-${Date.now()}-${Math.random()}`,
                                  fileName: file.name,
                                  blob,
                                  docType: 'UPLOADED',
                                  addedAt: new Date().toISOString()
                                }]);
                              }
                            }
                            toast.success(`Added ${files.length} document(s)`);
                          }}
                        />
                        <Button variant="outline" size="sm" asChild>
                          <span><Upload className="w-4 h-4 mr-2" />Upload PDF</span>
                        </Button>
                      </label>
                    </div>
                    
                    {customDocuments.length === 0 ? (
                      <p className="text-zinc-500 text-sm text-center py-4">No documents added yet</p>
                    ) : (
                      <div className="space-y-2">
                        {customDocuments.map((doc, idx) => (
                          <div key={doc.id} className="flex items-center justify-between p-2 bg-zinc-700 rounded">
                            <span className="text-sm text-zinc-300">{idx + 1}. {doc.fileName}</span>
                            {!doc.isBlankPage && (
                              <Button variant="ghost" size="sm" onClick={() => setCustomDocuments(prev => prev.filter(d => d.id !== doc.id))}>
                                <X className="w-4 h-4 text-red-400" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Mail Type & Send */}
                  <div className="flex items-center justify-between pt-4">
                    <select
                      value={customMailType}
                      onChange={(e) => setCustomMailType(e.target.value)}
                      className="bg-zinc-800 border border-zinc-600 text-white rounded px-3 py-2"
                    >
                      {mailTypeOptions.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label} - {opt.cost}</option>
                      ))}
                    </select>
                    <Button
                      className="bg-green-600 hover:bg-green-700"
                      disabled={sendingCustom || !customRecipient.name || !customRecipient.address_line1 || !customReturnAddress.name || !customReturnAddress.address_line1 || customDocuments.length < 2}
                      onClick={async () => {
                        setSendingCustom(true);
                        try {
                          // Merge PDFs and send via Lob
                          const formData = new FormData();
                          for (const doc of customDocuments) {
                            formData.append('documents', doc.blob, doc.fileName);
                          }
                          formData.append('recipient', JSON.stringify(customRecipient));
                          formData.append('mail_type', customMailType);
                          formData.append('return_address', JSON.stringify({
                            name: customReturnAddress.name,
                            address_line1: customReturnAddress.address_line1,
                            city: customReturnAddress.city,
                            state: customReturnAddress.state,
                            zip: customReturnAddress.zip
                          }));
                          
                          const response = await axios.post(`${API}/api/mail/send-custom`, formData);
                          if (response.data.success) {
                            toast.success('Mail sent successfully!');
                            // Save to local history
                            const historyEntry = {
                              mail_id: response.data.mail_id || Date.now().toString(),
                              recipient_name: customRecipient.name,
                              recipient_address: `${customRecipient.address_line1}, ${customRecipient.city}, ${customRecipient.state} ${customRecipient.zip}`,
                              recipient_city: customRecipient.city,
                              recipient_state: customRecipient.state,
                              recipient_zip: customRecipient.zip,
                              status: 'sent',
                              created_at: new Date().toISOString(),
                              cost: response.data.cost
                            };
                            const existingHistory = JSON.parse(localStorage.getItem('just_mail_history') || '[]');
                            localStorage.setItem('just_mail_history', JSON.stringify([historyEntry, ...existingHistory]));
                          } else {
                            toast.error(response.data.message || 'Failed to send mail');
                          }
                        } catch (err) {
                          toast.error('Error sending mail: ' + (err.response?.data?.message || err.message));
                        }
                        setSendingCustom(false);
                      }}
                    >
                      {sendingCustom ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                      Prepare & Download for USPS
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Show bureau sections only in bureaus mode */}
            {mailMode === 'bureaus' && (
              <>
                {/* Instructions */}
                <Card className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 border-amber-500/30">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />
                      <div className="text-sm">
                        <p className="text-amber-200 font-medium">How it works:</p>
                        <ol className="text-zinc-400 mt-2 space-y-1 list-decimal list-inside">
                          <li>Generate a Handwritten Declaration with client info (or upload your own)</li>
                          <li>Upload PDF documents to each bureau section</li>
                          <li>Drag to reorder documents (page 1 will be blank for the address label)</li>
                          <li>Select mail type (First Class, Certified, etc.)</li>
                          <li>Preview to verify document order</li>
                          <li>Click Prepare & Download to get a print-ready PDF</li>
                          <li>Print at home or Staples/UPS Store</li>
                          <li>Drop at USPS with Certified Mail + Return Receipt</li>
                          <li>Paste the USPS tracking number back in Mail History</li>
                        </ol>
                      </div>
                    </div>
                  </CardContent>
                </Card>

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* HANDWRITTEN DECLARATION GENERATOR */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        <Card className="border-2 border-purple-500/50 bg-gradient-to-r from-purple-500/5 to-indigo-500/5">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-purple-500/20 rounded-lg">
                  <PenTool className="w-5 h-5 text-purple-400" />
                </div>
                <div>
                  <CardTitle className="text-lg text-white">Handwritten Declaration Generator</CardTitle>
                  <p className="text-sm text-zinc-400">Generate a declaration stating you are not using a third-party credit repair company</p>
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDeclarationExpanded(!declarationExpanded)}
                className="text-zinc-400"
              >
                {declarationExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </Button>
            </div>
          </CardHeader>
          
          {declarationExpanded && (
            <CardContent className="pt-2 space-y-4">
              {/* Paste Area with Parse Button */}
              <div>
                <Label className="text-zinc-300 text-sm mb-2 block">
                  Step 1: Paste Client Info
                </Label>
                <div className="flex gap-2">
                  <Textarea
                    value={pasteText}
                    onChange={(e) => setPasteText(e.target.value)}
                    placeholder="Paste client info (7 or 9 lines):

7-LINE FORMAT:
Jennifer Graham
06/16/1986
252750546
13111 Largo Dr
Savannah GA 31419
912-755-4390
jennbg@gmail.com

9-LINE FORMAT:
John Doe
01/15/1985
123-45-6789
123 Main St
Los Angeles
CA
90001
(555) 123-4567
john.doe@email.com"
                    className="bg-zinc-800 border-zinc-700 text-white min-h-[140px] font-mono text-sm flex-1"
                  />
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <Button
                    onClick={handleParseClick}
                    variant="outline"
                    className="border-purple-500 text-purple-400 hover:bg-purple-500/10"
                    data-testid="parse-info-btn"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Parse Info
                  </Button>
                  <span className="text-xs text-zinc-500">
                    Supports 7-line (City State Zip combined) or 9-line (separate) format
                  </span>
                </div>
              </div>
              
              {/* Manual Entry Fields */}
              <div>
                <Label className="text-zinc-300 text-sm mb-2 block">
                  Step 2: Verify/Edit Client Info (this will be on the letter)
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <Label className="text-zinc-400 text-xs flex items-center gap-1 mb-1">
                    <User className="w-3 h-3" /> Full Legal Name *
                  </Label>
                  <Input
                    value={clientInfo.name}
                    onChange={(e) => setClientInfo(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="JOHN DOE"
                    className="bg-zinc-800 border-zinc-700 text-white uppercase"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs flex items-center gap-1 mb-1">
                    <Calendar className="w-3 h-3" /> Date of Birth
                  </Label>
                  <Input
                    value={clientInfo.dob}
                    onChange={(e) => setClientInfo(prev => ({ ...prev, dob: e.target.value }))}
                    placeholder="MM/DD/YYYY"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs flex items-center gap-1 mb-1">
                    <CreditCard className="w-3 h-3" /> Social Security Number
                  </Label>
                  <Input
                    value={clientInfo.ssn}
                    onChange={(e) => setClientInfo(prev => ({ ...prev, ssn: e.target.value }))}
                    placeholder="XXX-XX-XXXX"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs flex items-center gap-1 mb-1">
                    <MapPin className="w-3 h-3" /> Street Address
                  </Label>
                  <Input
                    value={clientInfo.address}
                    onChange={(e) => setClientInfo(prev => ({ ...prev, address: e.target.value }))}
                    placeholder="123 Main Street"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs mb-1">City</Label>
                  <Input
                    value={clientInfo.city}
                    onChange={(e) => setClientInfo(prev => ({ ...prev, city: e.target.value }))}
                    placeholder="Los Angeles"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-zinc-400 text-xs mb-1">State</Label>
                    <Input
                      value={clientInfo.state}
                      onChange={(e) => setClientInfo(prev => ({ ...prev, state: e.target.value.toUpperCase() }))}
                      placeholder="CA"
                      maxLength={2}
                      className="bg-zinc-800 border-zinc-700 text-white uppercase"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs mb-1">Zip Code</Label>
                    <Input
                      value={clientInfo.zip}
                      onChange={(e) => setClientInfo(prev => ({ ...prev, zip: e.target.value }))}
                      placeholder="90001"
                      maxLength={10}
                      className="bg-zinc-800 border-zinc-700 text-white"
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs mb-1">Phone Number</Label>
                  <Input
                    value={clientInfo.phone}
                    onChange={(e) => setClientInfo(prev => ({ ...prev, phone: e.target.value }))}
                    placeholder="(555) 123-4567"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs mb-1">Email Address</Label>
                  <Input
                    value={clientInfo.email}
                    onChange={(e) => setClientInfo(prev => ({ ...prev, email: e.target.value }))}
                    placeholder="john.doe@email.com"
                    className="bg-zinc-800 border-zinc-700 text-white"
                  />
                </div>
              </div>
              </div>
              
              {/* Bureau Selection Checkboxes */}
              <div className="pt-3 border-t border-zinc-700">
                <Label className="text-zinc-300 text-sm mb-3 block">
                  Step 3: Select Bureaus for Document Generation
                </Label>
                <div className="flex flex-wrap gap-4">
                  {BUREAUS.map(bureau => (
                    <label key={bureau.key} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedBureaus[bureau.key]}
                        onChange={() => toggleBureauSelection(bureau.key)}
                        className="w-4 h-4 text-purple-600 bg-zinc-800 border-zinc-600 rounded focus:ring-purple-500"
                      />
                      <span className={`text-sm font-medium ${bureau.textColor}`}>
                        {bureau.name}
                      </span>
                    </label>
                  ))}
                </div>
              </div>
              
              {/* Signature Style Selection - Using CursiveSignatureSelector */}
              <div className="pt-3 border-t border-zinc-700">
                <Label className="text-zinc-300 text-sm mb-3 block">
                  Step 3.5: Select Signature Style
                </Label>
                <CursiveSignatureSelector
                  clientName={clientInfo.name || 'John Doe'}
                  selectedStyleId={selectedSignatureStyleId}
                  onSelect={(selection) => {
                    setSelectedSignatureStyleId(selection.styleId);
                    setSignatureImageData(selection.dataUrl);
                  }}
                />
                {signatureImageData && (
                  <div className="mt-3 p-3 bg-zinc-800/50 rounded-lg border border-zinc-700">
                    <p className="text-xs text-zinc-400 mb-2">Signature Preview:</p>
                    <img 
                      src={signatureImageData} 
                      alt="Selected signature" 
                      className="max-h-16 bg-white rounded"
                      data-testid="signature-preview"
                    />
                  </div>
                )}
              </div>
              
              {/* Cover Letter Generation */}
              <div className="pt-3 border-t border-zinc-700">
                <Label className="text-zinc-300 text-sm mb-3 block">
                  Step 4: Generate Cover Letter
                </Label>
                <div className="flex flex-wrap items-center gap-4">
                  <Button
                    onClick={generateCoverLetter}
                    disabled={generatingCoverLetter || !clientInfo.name || getSelectedBureausList().length === 0}
                    className="bg-blue-600 hover:bg-blue-700 font-bold px-6"
                    data-testid="generate-cover-letter-btn"
                  >
                    {generatingCoverLetter ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Generate Cover Letter
                      </>
                    )}
                  </Button>
                  <span className="text-xs text-zinc-500">
                    Professional cover letter with &quot;ATTENTION DISPUTE DEPARTMENT&quot; header
                  </span>
                </div>
              </div>
              
              {/* Generate & Download Declaration */}
              <div className="pt-3 border-t border-zinc-700">
                <Label className="text-zinc-300 text-sm mb-3 block">
                  Step 5: Generate Handwritten Declaration
                </Label>
                <div className="flex flex-wrap items-center gap-4">
                  <Button
                    onClick={generateDeclaration}
                    disabled={generatingDeclaration || !clientInfo.name || getSelectedBureausList().length === 0}
                    className="bg-green-600 hover:bg-green-700 font-bold px-6"
                    data-testid="generate-declaration-btn"
                  >
                    {generatingDeclaration ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4 mr-2" />
                        Generate Declaration
                      </>
                    )}
                  </Button>
                  <span className="text-xs text-zinc-500">
                    Self-representation declaration under penalty of perjury
                  </span>
                </div>
                {clientInfo.name && (
                  <div className="mt-3 p-3 bg-zinc-800/50 rounded-lg">
                    <p className="text-sm text-zinc-300">
                      <span className="text-green-400 font-semibold">Return Address on Letter:</span>
                    </p>
                    <p className="text-sm text-white mt-1 font-mono">
                      {clientInfo.name.toUpperCase()}<br/>
                      {clientInfo.address && <>{clientInfo.address.toUpperCase()}<br/></>}
                      {(clientInfo.city || clientInfo.state || clientInfo.zip) && 
                        <>{[clientInfo.city, `${clientInfo.state} ${clientInfo.zip}`].filter(p => p && p.trim()).join(', ').toUpperCase()}</>
                      }
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          )}
        </Card>

        {/* Bureau Sections - Only show if bureau is selected */}
        <div className="space-y-4">
          {/* Show message if no bureaus selected */}
          {!Object.values(selectedBureaus).some(v => v) && (
            <Card className="border-2 border-amber-500/50 bg-amber-500/10">
              <CardContent className="py-8 text-center">
                <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-3" />
                <p className="text-amber-400 font-medium">No bureaus selected</p>
                <p className="text-zinc-400 text-sm mt-1">Check at least one bureau above to see the mail drop sections</p>
              </CardContent>
            </Card>
          )}
          
          {BUREAUS.filter(bureau => selectedBureaus[bureau.key]).map((bureau) => {
            const docs = documents[bureau.key];
            const isExpanded = expandedBureaus[bureau.key];
            const hasDocs = docs.length > 0;
            const result = mailResults[bureau.key];
            const isDragOver = dragOverBureau === bureau.key;
            
            return (
              <Card 
                key={bureau.key}
                className={`border-2 transition-all ${
                  hasDocs 
                    ? `${bureau.borderColor} bg-zinc-900/80` 
                    : isDragOver
                      ? `${bureau.borderColor} bg-zinc-800/50`
                      : 'border-zinc-700 bg-zinc-900/50'
                }`}
                data-testid={`mail-section-${bureau.key.toLowerCase()}`}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Badge className={`${bureau.color} text-white font-bold px-3 py-1`}>
                        {bureau.name}
                      </Badge>
                      <Badge variant="outline" className={hasDocs ? 'text-green-400 border-green-500' : 'text-zinc-500 border-zinc-600'}>
                        {docs.length} document{docs.length !== 1 ? 's' : ''}
                      </Badge>
                      <span className="text-xs text-zinc-500">{bureau.address}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {hasDocs && !result?.success && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => previewPackage(bureau.key)}
                            disabled={previewLoading[bureau.key]}
                            className={`${bureau.borderColor} border hover:bg-zinc-800`}
                          >
                            {previewLoading[bureau.key] ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Eye className="w-4 h-4 mr-1" />
                                Preview
                              </>
                            )}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => downloadPackage(bureau.key)}
                            disabled={downloadLoading[bureau.key]}
                            className="border-green-500 text-green-400 hover:bg-green-500/10"
                          >
                            {downloadLoading[bureau.key] ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <Download className="w-4 h-4 mr-1" />
                                Download
                              </>
                            )}
                          </Button>
                        </>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleBureauExpand(bureau.key)}
                        className="text-zinc-400"
                      >
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                
                {isExpanded && (
                  <CardContent className="pt-2">
                    {/* Upload Zone */}
                    <div
                      onDrop={handleDrop(bureau.key)}
                      onDragOver={handleDragOver(bureau.key)}
                      onDragLeave={handleDragLeave}
                      className={`border-2 border-dashed rounded-lg p-6 mb-4 transition-all text-center ${
                        isDragOver 
                          ? `${bureau.borderColor} bg-zinc-800/50` 
                          : 'border-zinc-700 hover:border-zinc-600'
                      }`}
                    >
                      <Upload className={`w-10 h-10 ${bureau.textColor} mx-auto mb-3 opacity-60`} />
                      <p className="text-zinc-400 mb-2">Drop PDF files here or click to browse</p>
                      <label>
                        <input
                          type="file"
                          accept=".pdf"
                          multiple
                          onChange={handleFileUpload(bureau.key)}
                          className="hidden"
                        />
                        <Button variant="outline" size="sm" className="cursor-pointer" asChild>
                          <span>
                            <Plus className="w-4 h-4 mr-2" />
                            Add Documents
                          </span>
                        </Button>
                      </label>
                    </div>
                    
                    {/* Experian Upload Online Option - Only for EXPERIAN */}
                    {bureau.key === 'EXPERIAN' && (
                      <div className="mb-4 p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                          <div>
                            <p className="text-blue-300 font-medium text-sm">Upload directly to Experian</p>
                            <p className="text-xs text-zinc-400">Upload documents online instead of mailing</p>
                          </div>
                          <Button
                            onClick={openExperianUpload}
                            className="bg-blue-600 hover:bg-blue-700 font-semibold"
                            data-testid="experian-upload-btn"
                          >
                            <ExternalLink className="w-4 h-4 mr-2" />
                            Upload to Experian
                          </Button>
                        </div>
                      </div>
                    )}
                    
                    {hasDocs ? (
                      <>
                        {/* Document List */}
                        <div className="mb-3 px-2 flex justify-between items-center">
                          <span className="text-xs text-zinc-400 uppercase tracking-wide">
                            Document Order (drag to reorder)
                          </span>
                          <span className="text-xs text-green-400">Total: {docs.length} pages</span>
                        </div>
                        
                        <div className="space-y-2 mb-4">
                          {docs.map((doc, index) => (
                            <div
                              key={doc.id}
                              draggable
                              onDragStart={handleDocDragStart(bureau.key, doc, index)}
                              onDragOver={handleDocDragOver(bureau.key, index)}
                              onDrop={handleDocDrop(bureau.key, index)}
                              onDragEnd={handleDocDragEnd}
                              className={`flex items-center gap-3 p-3 rounded-lg border transition-all cursor-grab active:cursor-grabbing ${
                                draggedDoc?.doc?.id === doc.id && draggedBureau === bureau.key
                                  ? 'border-yellow-500 bg-yellow-500/10 opacity-50'
                                  : 'border-zinc-700 bg-zinc-800/50 hover:border-zinc-600 hover:bg-zinc-800'
                              }`}
                            >
                              <GripVertical className="w-4 h-4 text-zinc-500 flex-shrink-0" />
                              
                              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                                doc.isBlankPage ? 'bg-gray-600' : bureau.color
                              }`}>
                                {index + 1}
                              </div>
                              
                              <div className="flex-1 min-w-0">
                                <p className="text-white font-medium truncate">
                                  {doc.isBlankPage ? '📄 BLANK PAGE (Lob prints address here)' : doc.fileName}
                                </p>
                              </div>
                              
                              <div className="flex items-center gap-1 flex-shrink-0">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => moveDocumentUp(bureau.key, index)}
                                  disabled={index === 0}
                                  className="h-7 w-7 p-0 text-zinc-400 hover:text-white disabled:opacity-30"
                                >
                                  <ArrowUp className="w-3 h-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => moveDocumentDown(bureau.key, index)}
                                  disabled={index === docs.length - 1}
                                  className="h-7 w-7 p-0 text-zinc-400 hover:text-white disabled:opacity-30"
                                >
                                  <ArrowDown className="w-3 h-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => previewDocument(doc)}
                                  className="h-7 w-7 p-0 text-zinc-400 hover:text-cyan-400"
                                >
                                  <Eye className="w-3 h-3" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeDocument(bureau.key, doc.id)}
                                  className="h-7 w-7 p-0 text-zinc-400 hover:text-red-400"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                        
                        {/* Mail Type & Send */}
                        {!result?.success && (
                          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 p-3 bg-zinc-800/50 rounded-lg">
                            <div className="flex-1">
                              <Label className="text-xs text-zinc-400 mb-1 block">Mail Type</Label>
                              <select
                                value={bureauMailTypes[bureau.key]}
                                onChange={(e) => setBureauMailTypes(prev => ({ ...prev, [bureau.key]: e.target.value }))}
                                className="w-full bg-zinc-900 border border-zinc-700 text-white rounded-lg px-3 py-2 text-sm"
                              >
                                {mailTypeOptions.map(opt => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label} ({opt.cost})
                                  </option>
                                ))}
                              </select>
                            </div>
                            <Button
                              onClick={() => sendMailViaLob(bureau.key)}
                              disabled={sending[bureau.key] || sendingAll}
                              className={`${bureau.color} hover:opacity-90 font-bold px-6`}
                              data-testid={`send-lob-${bureau.key.toLowerCase()}`}
                            >
                              {sending[bureau.key] ? (
                                <>
                                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                  Sending...
                                </>
                              ) : (
                                <>
                                  <Send className="w-4 h-4 mr-2" />
                                  Prepare & Download for USPS
                                </>
                              )}
                            </Button>
                          </div>
                        )}
                        
                        {/* Success Result */}
                        {result?.success && (
                          <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg text-center">
                            <CheckCircle2 className="w-10 h-10 text-green-400 mx-auto mb-2" />
                            <p className="text-green-300 font-semibold">Mail Sent Successfully!</p>
                            {result.data?.tracking_number && (
                              <p className="text-zinc-400 text-sm mt-1">
                                Tracking: <span className="font-mono">{result.data.tracking_number}</span>
                              </p>
                            )}
                          </div>
                        )}
                        
                        {/* Error Result */}
                        {result && !result.success && result.error && (
                          <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg text-center">
                            <X className="w-10 h-10 text-red-400 mx-auto mb-2" />
                            <p className="text-red-300 font-semibold">Failed to Send</p>
                            <p className="text-zinc-400 text-sm mt-1">{result.error}</p>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setMailResults(prev => ({ ...prev, [bureau.key]: null }))}
                              className="mt-3"
                            >
                              Retry
                            </Button>
                          </div>
                        )}
                        
                        {/* Package Summary */}
                        <div className="mt-3 p-3 bg-zinc-800/30 rounded-lg">
                          <div className="flex items-center gap-2 text-sm">
                            <Package className="w-4 h-4 text-green-400" />
                            <span className="text-zinc-300">
                              Package: <span className="text-white font-semibold">{docs.length} page{docs.length !== 1 ? 's' : ''}</span> total (including blank page for Lob address)
                            </span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-4 text-zinc-500">
                        <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                        <p>No documents uploaded yet</p>
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>

        {/* Send All Section */}
        {getTotalDocs() > 0 && getReadyPacketsCount() > 0 && (
          <Card className="bg-gradient-to-r from-green-500/10 to-emerald-500/10 border-green-500/30">
            <CardContent className="p-4">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Package className="w-5 h-5 text-green-400" />
                    <span className="text-green-200 font-semibold">
                      {getReadyPacketsCount()} Package{getReadyPacketsCount() !== 1 ? 's' : ''} Ready
                    </span>
                  </div>
                  <Badge variant="outline" className="text-green-400 border-green-400">
                    Est. ${getTotalCostEstimate()}
                  </Badge>
                </div>
                
                <Button
                  onClick={sendAllBureaus}
                  disabled={sendingAll}
                  className="bg-green-600 hover:bg-green-700 font-bold px-8"
                  data-testid="send-all-bureaus-btn"
                >
                  {sendingAll ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Sending All...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send All via Lob
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Footer Info */}
        <Card className="bg-zinc-800/50 border-zinc-700">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Truck className="w-5 h-5 text-amber-400 mt-0.5" />
              <div>
                <h4 className="text-white font-medium">Mailing Information</h4>
                <ul className="text-sm text-zinc-400 mt-2 space-y-1">
                  <li>• First Class: 3-5 business days</li>
                  <li>• Certified Mail: 5-7 business days with tracking</li>
                  <li>• Certified + Return Receipt: Includes proof of delivery</li>
                  <li>• All mail sent via Lob.com USPS integration</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
              </>
            )}
          </>
        )}

        {/* ═══════════════════════════════════════════════════════════════════ */}
        {/* EXPERIAN UPLOAD HELPER MODAL */}
        {/* Shows client info fields to copy for the Experian upload form */}
        {/* ═══════════════════════════════════════════════════════════════════ */}
        {showExperianUploadModal && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <Card className="bg-zinc-900 border-blue-500 max-w-lg w-full max-h-[90vh] overflow-y-auto">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500/20 rounded-lg">
                      <ExternalLink className="w-5 h-5 text-blue-400" />
                    </div>
                    <div>
                      <CardTitle className="text-lg text-white">Experian Upload Helper</CardTitle>
                      <p className="text-sm text-zinc-400">Copy client info to paste into the Experian form</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowExperianUploadModal(false)}
                    className="text-zinc-400 hover:text-white"
                  >
                    <X className="w-5 h-5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-amber-400 bg-amber-500/10 p-2 rounded">
                  The Experian upload page should have opened in a new tab. Use the copy buttons below to easily paste client info into the form fields.
                </p>
                
                {/* First Name */}
                <div className="flex items-center gap-2 p-2 bg-zinc-800 rounded">
                  <div className="flex-1">
                    <p className="text-xs text-zinc-400">First Name</p>
                    <p className="text-white font-mono">{getNameParts().firstName || '-'}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(getNameParts().firstName, 'First Name')}
                    className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                  >
                    {copiedField === 'First Name' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                
                {/* Middle Initial */}
                {getNameParts().middleInitial && (
                  <div className="flex items-center gap-2 p-2 bg-zinc-800 rounded">
                    <div className="flex-1">
                      <p className="text-xs text-zinc-400">Middle Initial</p>
                      <p className="text-white font-mono">{getNameParts().middleInitial}</p>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => copyToClipboard(getNameParts().middleInitial, 'Middle Initial')}
                      className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                    >
                      {copiedField === 'Middle Initial' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                )}
                
                {/* Last Name */}
                <div className="flex items-center gap-2 p-2 bg-zinc-800 rounded">
                  <div className="flex-1">
                    <p className="text-xs text-zinc-400">Last Name</p>
                    <p className="text-white font-mono">{getNameParts().lastName || '-'}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(getNameParts().lastName, 'Last Name')}
                    className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                  >
                    {copiedField === 'Last Name' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                
                {/* Address */}
                <div className="flex items-center gap-2 p-2 bg-zinc-800 rounded">
                  <div className="flex-1">
                    <p className="text-xs text-zinc-400">Address</p>
                    <p className="text-white font-mono">{clientInfo.address || '-'}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(clientInfo.address, 'Address')}
                    className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                  >
                    {copiedField === 'Address' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                
                {/* City */}
                <div className="flex items-center gap-2 p-2 bg-zinc-800 rounded">
                  <div className="flex-1">
                    <p className="text-xs text-zinc-400">City</p>
                    <p className="text-white font-mono">{clientInfo.city || '-'}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(clientInfo.city, 'City')}
                    className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                  >
                    {copiedField === 'City' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                
                {/* State */}
                <div className="flex items-center gap-2 p-2 bg-zinc-800 rounded">
                  <div className="flex-1">
                    <p className="text-xs text-zinc-400">State</p>
                    <p className="text-white font-mono">{clientInfo.state || '-'}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(clientInfo.state, 'State')}
                    className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                  >
                    {copiedField === 'State' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                
                {/* ZIP Code */}
                <div className="flex items-center gap-2 p-2 bg-zinc-800 rounded">
                  <div className="flex-1">
                    <p className="text-xs text-zinc-400">ZIP Code</p>
                    <p className="text-white font-mono">{clientInfo.zip || '-'}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(clientInfo.zip, 'ZIP Code')}
                    className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                  >
                    {copiedField === 'ZIP Code' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                
                {/* Social Security Number */}
                <div className="flex items-center gap-2 p-2 bg-zinc-800 rounded border border-red-500/30">
                  <div className="flex-1">
                    <p className="text-xs text-red-400">Social Security Number / ITIN</p>
                    <p className="text-white font-mono">{clientInfo.ssn || '-'}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(clientInfo.ssn, 'SSN')}
                    className="border-red-500 text-red-400 hover:bg-red-500/10"
                  >
                    {copiedField === 'SSN' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                
                {/* Email */}
                <div className="flex items-center gap-2 p-2 bg-zinc-800 rounded">
                  <div className="flex-1">
                    <p className="text-xs text-zinc-400">Email Address</p>
                    <p className="text-white font-mono">{clientInfo.email || '-'}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyToClipboard(clientInfo.email, 'Email')}
                    className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                  >
                    {copiedField === 'Email' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                
                <div className="pt-3 border-t border-zinc-700">
                  <Button
                    onClick={() => setShowExperianUploadModal(false)}
                    className="w-full bg-zinc-700 hover:bg-zinc-600"
                  >
                    Done
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
        
        {/* MAIL CONFIRMATION DIALOG */}
        <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-700 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl text-yellow-400 flex items-center gap-2">
                <AlertTriangle className="w-6 h-6" />
                Confirm Mail Send
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                Please review the cost before sending mail to Lob.com
              </DialogDescription>
            </DialogHeader>
            
            {costEstimate && (
              <div className="space-y-4 py-4">
                <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-zinc-400">Client:</span>
                    <span className="text-white font-medium">{costEstimate.client_name}</span>
                  </div>
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-zinc-400">Bureau:</span>
                    <span className="text-white font-medium">{costEstimate.bureau}</span>
                  </div>
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-zinc-400">Mail Type:</span>
                    <span className="text-white font-medium capitalize">{costEstimate.mail_type?.replace(/_/g, ' ')}</span>
                  </div>
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-zinc-400">Documents:</span>
                    <span className="text-white font-medium">{costEstimate.document_count}</span>
                  </div>
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-zinc-400">Pages (est):</span>
                    <span className="text-white font-medium">{costEstimate.page_count}</span>
                  </div>
                  
                  <div className="border-t border-zinc-600 pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-lg text-yellow-400 font-semibold">Estimated Cost:</span>
                      <span className="text-2xl text-green-400 font-bold">
                        ${costEstimate.estimated_cost?.toFixed(2)}
                      </span>
                    </div>
                    {costEstimate.cost_breakdown && (
                      <div className="mt-2 text-xs text-zinc-500">
                        Base fee: ${costEstimate.cost_breakdown.base_fee} + 
                        ${costEstimate.cost_breakdown.per_page_fee}/page × {costEstimate.cost_breakdown.pages} pages
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                  <p className="text-yellow-400 text-sm">
                    <AlertTriangle className="w-4 h-4 inline mr-2" />
                    This will charge your Lob.com account and send physical mail.
                  </p>
                </div>
              </div>
            )}
            
            <DialogFooter className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setConfirmDialogOpen(false);
                  setCostEstimate(null);
                  setPendingMailBureau(null);
                }}
                className="border-zinc-600 text-zinc-300"
              >
                Cancel
              </Button>
              <Button 
                onClick={confirmAndSendMail}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Send className="w-4 h-4 mr-2" />
                Confirm & Send (${costEstimate?.estimated_cost?.toFixed(2)})
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* BATCH MAIL CONFIRMATION DIALOG */}
        <Dialog open={batchConfirmOpen} onOpenChange={setBatchConfirmOpen}>
          <DialogContent className="bg-zinc-900 border-zinc-700 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-xl text-yellow-400 flex items-center gap-2">
                <AlertTriangle className="w-6 h-6" />
                Confirm Batch Mail Send
              </DialogTitle>
              <DialogDescription className="text-zinc-400">
                Review costs for all bureaus before sending
              </DialogDescription>
            </DialogHeader>
            
            {batchEstimates.length > 0 && (
              <div className="space-y-4 py-4">
                <div className="bg-zinc-800 rounded-lg p-4 border border-zinc-700 max-h-64 overflow-y-auto">
                  {batchEstimates.map((estimate, idx) => (
                    <div key={idx} className="flex justify-between items-center py-2 border-b border-zinc-700 last:border-0">
                      <div>
                        <span className="text-white font-medium">{estimate.bureau}</span>
                        <p className="text-xs text-zinc-500">{estimate.document_count} docs, ~{estimate.page_count} pages</p>
                      </div>
                      <span className="text-green-400 font-bold">${estimate.estimated_cost?.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
                
                <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4">
                  <div className="flex justify-between items-center">
                    <span className="text-lg text-white font-semibold">Total Estimated Cost:</span>
                    <span className="text-2xl text-green-400 font-bold">
                      ${batchEstimates.reduce((sum, e) => sum + (e.estimated_cost || 0), 0).toFixed(2)}
                    </span>
                  </div>
                </div>
                
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                  <p className="text-yellow-400 text-sm">
                    <AlertTriangle className="w-4 h-4 inline mr-2" />
                    This will charge your Lob.com account for {batchEstimates.length} letter(s).
                  </p>
                </div>
              </div>
            )}
            
            <DialogFooter className="flex gap-2">
              <Button 
                variant="outline" 
                onClick={() => {
                  setBatchConfirmOpen(false);
                  setBatchEstimates([]);
                }}
                className="border-zinc-600 text-zinc-300"
              >
                Cancel
              </Button>
              <Button 
                onClick={confirmAndSendAllBureaus}
                className="bg-green-600 hover:bg-green-700 text-white"
              >
                <Send className="w-4 h-4 mr-2" />
                Confirm & Send All (${batchEstimates.reduce((sum, e) => sum + (e.estimated_cost || 0), 0).toFixed(2)})
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
